// features/superAdmin/components/AssetUploadComponent.tsx
import React, { useState, useRef, useEffect } from "react";
import axios from "axios";
import { API_ROOT_URL } from "../../config/api";
import {
  clearAssetUploadFailures,
  recordAssetUploadFailure,
} from "../../utils/assetFailureEscalation";

// ============================================================
// TYPES - Combined from both branches
// ============================================================

interface MetadataSchema {
  assetId: string;
  originalFileName: string;
  uploadDateTime: string;
  uploadedBy: string;
  fileType: string;
  collectionId?: string;
  thumbnailUrl?: string;
  codecDetails: string;
  status: "valid" | "invalid" | "processing" | "pending";
  fileSize: number;
  checksum: string;
  assetDescription?: string;
}

interface UploadLogEntry {
  timestamp: string;
  fileName: string;
  fileSize: number;
  fileType: string;
  attemptNumber: number;
  status: string;
  fileCategory?: string;
  videoDuration?: number;
  codecDetails?: string;
  checksum?: string;
  checksumError?: string;
  duplicateWarning?: boolean;
  duplicateError?: string;
  durationWarning?: string;
  durationError?: string;
  codecError?: string;
  metadataError?: string;
  metadataExtractionError?: string;
  extractedMetadata?: {
    status: string;
    missingFields?: string[];
    errors?: string[];
  };
  imageDimensions?: string;
  audioBitrate?: number;
  thumbnailGenerated?: boolean;
  formatError?: string;
  sizeError?: string;
  errorReason?: string;
  assetId?: string;
  finalStatus?: string;
  ffmpegAvailable?: boolean;
}

interface ExtractedMetadata {
  fileName: string;
  fileSize: number;
  fileType: string;
  mimeType: string;
  fileExtension: string;
  lastModified: Date;
  imageWidth?: number;
  imageHeight?: number;
  imageColorSpace?: string;
  imageBitsPerPixel?: number;
  imageCompression?: string;
  videoDuration?: number;
  videoWidth?: number;
  videoHeight?: number;
  videoFrameRate?: number;
  videoBitrate?: number;
  videoCodec?: string;
  videoAspectRatio?: string;
  audioDuration?: number;
  audioBitrate?: number;
  audioSampleRate?: number;
  audioChannels?: number;
  audioCodec?: string;
  documentPages?: number;
  documentAuthor?: string;
  documentCreator?: string;
  documentProducer?: string;
  documentCreationDate?: string;
  documentModificationDate?: string;
  embeddedMetadata?: Record<string, any>;
  exifData?: Record<string, any>;
  iptcData?: Record<string, any>;
  xmpData?: Record<string, any>;
  formatName?: string;
  formatLongName?: string;
  formatBitrate?: number;
  extractionDate: string;
  extractionStatus: "complete" | "partial" | "failed";
  extractionErrors?: string[];
  missingFields?: string[];
}

interface AssetIdentity {
  uuid: string;
  checksum: string;
  originalName: string;
  firstUploadDate: string;
  currentVersion: number;
  versionHistory: AssetVersion[];
  linkedEvents: string[];
  linkedProducts: string[];
  isActive: boolean;
  approvalStatus:
    | "pending"
    | "approved"
    | "rejected"
    | "modification_requested";
  approvalHistory: ApprovalRecord[];
}

interface AssetVersion {
  version: number;
  uploadDate: string;
  fileSize: number;
  fileType: string;
  checksum: string;
  s3Key: string;
  metadata: MetadataSchema;
  extractedMetadata?: ExtractedMetadata;
  changesDescription?: string;
  uploadedBy: string;
  status: "active" | "archived" | "pending_approval";
}

interface ApprovalRecord {
  approvalId: string;
  timestamp: string;
  requestedBy: string;
  approvedBy?: string;
  status: "pending" | "approved" | "rejected";
  comments?: string;
  modificationReason: string;
  versionNumber: number;
}

interface UploadedAsset {
  id: string;
  parentAssetId?: string;
  uuid?: string;
  name: string;
  formattedName?: string;
  type: string;
  size: number;
  url: string;
  uploadDate: Date;
  status: "valid" | "invalid" | "pending" | "processing";
  errorReason?: string;
  validationErrors?: string[];
  file: File;
  retentionEndDate: Date;
  storageLocation: "temp" | "longterm" | "glacier" | "restoring" | "archiving";
  s3Key?: string;
  etag?: string;
  metadata?: MetadataSchema;
  extractedMetadata?: ExtractedMetadata;
  checksum?: string;
  version: number;
  parentId?: string;
  thumbnailUrl?: string;
  collectionId?: string;
  assetDescription?: string;
  isDuplicate?: boolean;
  duplicateGroupId?: string;
  assetIdentity?: AssetIdentity;
  linkedEvents?: string[];
  linkedProducts?: string[];
  approvalStatus?:
    | "pending"
    | "approved"
    | "rejected"
    | "modification_requested";
  isLatestVersion?: boolean;
  isArchived?: boolean;
  hasRetentionPolicy?: boolean;
  restoreAvailableUntil?: string;
  restoreDaysRemaining?: number;
  restoredAccessUrl?: string;
  restoreEstimate?: string;
  tenantId?: string;
  tenantName?: string;
}

interface AssetValidationResult {
  isValid: boolean;
  errorReason?: string;
  suggestedAction?: string;
  missingFields?: string[];
  validationErrors?: string[];
}

interface StorageMetrics {
  totalCapacity: number;
  usedCapacity: number;
  availableCapacity: number;
  tempStorageUsed: number;
  longTermStorageUsed: number;
  percentageUsed: number;
}

interface ValidationCheck {
  id: string;
  label: string;
  status: "pending" | "running" | "success" | "error" | "warning";
  message?: string;
  timestamp?: Date;
}

interface ApprovalModalState {
  show: boolean;
  assetId: string;
  assetName: string;
  action: "approve" | "reject" | "request_modification";
  comment: string;
}

// ============================================================
// MAIN COMPONENT
// ============================================================

interface CatalogSummary {
  totalCount: number;
  totalSize: number;
  tempCount: number;
  tempSize: number;
  longtermCount: number;
  longtermSize: number;
  glacierCount: number;
  glacierSize: number;
  restoringCount: number;
  restoringSize: number;
}

interface TenantOption {
  id: string;
  name: string;
}

interface ArchiveConfirmState {
  show: boolean;
  action: "archive" | "restore";
  asset: UploadedAsset | null;
}

const AssetUploadComponent: React.FC = () => {
  const getUploadFailureKey = (
    file: Pick<File, "name" | "size" | "lastModified">,
  ): string => `super:${file.name}:${file.size}:${file.lastModified}`;

  // ============================================================
  // STATE - Combined from both branches
  // ============================================================

  // Core asset state
  const [assets, setAssets] = useState<UploadedAsset[]>([]);
  const [invalidAssets, setInvalidAssets] = useState<UploadedAsset[]>([]);

  // Upload state
  const [isDragging, setIsDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  // UI state
  const [selectedTab, setSelectedTab] = useState<
    "valid" | "invalid" | "storage"
  >("valid");
  const [showRetentionInfo, setShowRetentionInfo] = useState(false);
  const [selectedAssetForDebug, setSelectedAssetForDebug] =
    useState<UploadedAsset | null>(null);

  // Storage metrics
  const [storageMetrics, setStorageMetrics] = useState<StorageMetrics>({
    totalCapacity: 10 * 1024 * 1024 * 1024,
    usedCapacity: 0,
    availableCapacity: 10 * 1024 * 1024 * 1024,
    tempStorageUsed: 0,
    longTermStorageUsed: 0,
    percentageUsed: 0,
  });

  // Duplicate detection
  const [showDuplicateDetection, setShowDuplicateDetection] = useState(false);
  const [duplicateCandidates, setDuplicateCandidates] = useState<
    UploadedAsset[]
  >([]);
  const [selectedDuplicates, setSelectedDuplicates] = useState<string[]>([]);

  // Upload form data
  const [uploadFormData, setUploadFormData] = useState<{
    collectionId: string;
    assetDescription: string;
    linkedEvents: string[];
    linkedProducts: string[];
  }>({
    collectionId: "",
    assetDescription: "",
    linkedEvents: [],
    linkedProducts: [],
  });

  // Validation Modal State
  const [showValidationModal, setShowValidationModal] = useState(false);
  const [currentFileIndex, setCurrentFileIndex] = useState(0);
  const [totalFiles, setTotalFiles] = useState(0);
  const [validationChecks, setValidationChecks] = useState<ValidationCheck[]>(
    [],
  );
  const [modalAssetName, setModalAssetName] = useState("");
  const [modalOverallStatus, setModalOverallStatus] = useState<
    "pending" | "validating" | "success" | "error"
  >("pending");
  const [uploadAttemptLogs, setUploadAttemptLogs] = useState<any[]>([]);
  const [lastValidationFiles, setLastValidationFiles] = useState<File[]>([]);

  // Metadata extraction modal
  const [showMetadataModal, setShowMetadataModal] = useState(false);
  const [selectedAssetForMetadata, setSelectedAssetForMetadata] =
    useState<UploadedAsset | null>(null);

  // Company ID
  // Tenant selection
  const [tenants, setTenants] = useState<TenantOption[]>([]);
  const [tenantsLoading, setTenantsLoading] = useState(false);
  const [filterTenantId, setFilterTenantId] = useState(""); // list filter (page)
  const [uploadTenantId, setUploadTenantId] = useState(""); // mandatory tenant inside the ingestion popup
  const [showIngestionModal, setShowIngestionModal] = useState(false);
  const [archiveConfirm, setArchiveConfirm] = useState<ArchiveConfirmState>({
    show: false,
    action: "archive",
    asset: null,
  });

  // Refs so interval callbacks always see the latest filter, and stale responses are ignored
  const filterTenantIdRef = useRef("");
  filterTenantIdRef.current = filterTenantId;
  const loadSeqRef = useRef(0);

  // Pagination + loader
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(8);
  const [totalAssets, setTotalAssets] = useState(0);
  const [assetsLoading, setAssetsLoading] = useState(true);
  const [catalogSummary, setCatalogSummary] = useState(
    null as CatalogSummary | null,
  );
  const currentPageRef = useRef(1);
  currentPageRef.current = currentPage;
  const pageSizeRef = useRef(8);
  pageSizeRef.current = pageSize;

  // Approval Modal
  const [approvalModal, setApprovalModal] = useState<ApprovalModalState>({
    show: false,
    assetId: "",
    assetName: "",
    action: "approve",
    comment: "",
  });

  // Version History Modal
  const [showVersionHistory, setShowVersionHistory] = useState(false);
  const [selectedAssetForVersions, setSelectedAssetForVersions] =
    useState<UploadedAsset | null>(null);

  // Glacier state (from development branch)
  const [retrieveFileName, setRetrieveFileName] = useState("");
  const [archiveActionId, setArchiveActionId] = useState<string | null>(null);
  const [archiveMessage, setArchiveMessage] = useState<{
    text: string;
    type: "success" | "error";
  } | null>(null);
  const [manualCloseModal, setManualCloseModal] = useState(false);
  const [allValidationResults, setAllValidationResults] = useState<any[]>([]);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const restorePollTimers = useRef<Map<string, ReturnType<typeof setTimeout>>>(
    new Map(),
  );

  useEffect(
    () => () => {
      restorePollTimers.current.forEach((timer) => clearTimeout(timer));
      restorePollTimers.current.clear();
    },
    [],
  );

  // ============================================================
  // API CONFIGURATION
  // ============================================================

  const ASSET_INGEST_URL = `${API_ROOT_URL}/v1/assets/ingest`;

  // ============================================================
  // USER & AUDIT HELPERS (from development branch)
  // ============================================================

  const getCurrentUser = () => {
    try {
      return JSON.parse(localStorage.getItem("user") || "null");
    } catch {
      return null;
    }
  };

  const getAuthHeaders = (
    extra: Record<string, string> = {},
  ): Record<string, string> => {
    const token = localStorage.getItem("accessToken");
    return token
      ? { ...extra, Authorization: `Bearer ${token}` }
      : { ...extra };
  };

  const loadTenants = async (): Promise<void> => {
    setTenantsLoading(true);
    try {
      const response = await fetch(`${API_ROOT_URL}/tenants`, {
        headers: getAuthHeaders(),
      });
      const result = await response.json().catch(() => null);
      if (!response.ok || !result?.success) {
        throw new Error(result?.message || "Failed to load tenants");
      }
      setTenants(result.data || []);
    } catch (error) {
      console.error("Failed to load tenants:", error);
      setArchiveMessage({
        text: error instanceof Error ? error.message : "Failed to load tenants",
        type: "error",
      });
    } finally {
      setTenantsLoading(false);
    }
  };

  const getAuditUser = () => {
    const currentUser = getCurrentUser();
    return {
      id: currentUser?.id,
      email: currentUser?.email,
      role: currentUser?.role,
      tenantId: currentUser?.tenantId,
    };
  };

  // ============================================================
  // LOCAL STORAGE LOADING
  // ============================================================

  useEffect(() => {
    loadAssetsFromStorage();
    void loadAssetsFromDatabase();
    calculateStorageMetrics();
    checkAndMoveExpiredAssets();
    detectDuplicates();
    loadUploadLogs();

    const interval = setInterval(() => {
      checkAndMoveExpiredAssets();
      calculateStorageMetrics();
    }, 3600000);
    const databaseRefreshInterval = setInterval(() => {
      void loadAssetsFromDatabase();
    }, 60000);

    return () => {
      clearInterval(interval);
      clearInterval(databaseRefreshInterval);
    };
  }, []);

  useEffect(() => {
    void loadAssetsFromDatabase();
  }, [filterTenantId, currentPage, pageSize]);

  // Show the loader whenever the tenant filter or the page changes
  useEffect(() => {
    setAssetsLoading(true);
  }, [filterTenantId, currentPage, pageSize]);

  useEffect(() => {
    void loadTenants();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Keep the Storage Monitor in sync with whatever tenant's assets are loaded
  useEffect(() => {
    calculateStorageMetrics();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assets, invalidAssets, catalogSummary]);

  const loadAssetsFromDatabase = async (): Promise<void> => {
    const seq = ++loadSeqRef.current;
    try {
      const response = await fetch(
        `${API_ROOT_URL}/v1/assets/catalog?page=${currentPageRef.current}&limit=${pageSizeRef.current}${filterTenantIdRef.current ? `&tenantId=${encodeURIComponent(filterTenantIdRef.current)}` : ""}`,
        { headers: getAuthHeaders() },
      );
      const result = await response.json().catch(() => null);
      if (!response.ok || !result?.success) {
        throw new Error(result?.message || "Failed to load assets");
      }

      if (seq !== loadSeqRef.current) return;
      setCatalogSummary(result.summary ?? null);
      const totalCount = Number(
        result.pagination?.total ?? result.summary?.totalCount ?? 0,
      );
      setTotalAssets(totalCount);
      const lastPage = Math.max(1, Math.ceil(totalCount / pageSizeRef.current));
      if (currentPageRef.current > lastPage) setCurrentPage(lastPage);

      const databaseAssets: UploadedAsset[] = (result.data || []).map(
        (row: any) => {
          const metadata = row.metadata || {};
          const storageLocation =
            row.is_archived && row.storage_location === "temp"
              ? "glacier"
              : [
                    "temp",
                    "longterm",
                    "glacier",
                    "restoring",
                    "archiving",
                  ].includes(row.storage_location)
                ? row.storage_location
                : "temp";
          return {
            id: row.id,
            parentAssetId: row.parent_asset_id,
            uuid: row.asset_uuid,
            tenantId: row.tenant_id,
            tenantName: row.tenant_name,
            name: row.original_name,
            formattedName: row.naming_format || undefined,
            type: row.mime_type || "application/octet-stream",
            size: Number(row.file_size || 0),
            url: metadata.url || "",
            uploadDate: new Date(row.created_at || row.upload_date),
            status: ["valid", "invalid", "pending", "processing"].includes(
              row.status,
            )
              ? row.status
              : "valid",
            file: new File([], row.original_name, {
              type: row.mime_type || "application/octet-stream",
            }),
            retentionEndDate: new Date(
              row.retention_end || row.created_at || row.upload_date,
            ),
            storageLocation,
            s3Key: row.source_key || metadata.s3Key || metadata.key,
            etag: metadata.eTag,
            checksum: row.checksum,
            version: row.version || 1,
            thumbnailUrl: row.thumbnail_data || metadata.thumbnailUrl,
            collectionId: row.collection_id,
            assetDescription: row.asset_description,
            linkedEvents: row.linked_events || [],
            linkedProducts: row.linked_products || [],
            approvalStatus: row.approval_status,
            isLatestVersion: row.is_latest,
            isArchived: row.is_archived === true,
            hasRetentionPolicy: Boolean(row.retention_end),
          } as UploadedAsset;
        },
      );

      await Promise.all(
        databaseAssets.map(async (asset) => {
          if (
            !asset.s3Key ||
            (!asset.type.startsWith("image/") &&
              !asset.type.startsWith("video/"))
          )
            return;
          try {
            const accessResponse = await fetch(
              `${API_ROOT_URL}/v1/assets/access-url?key=${encodeURIComponent(asset.s3Key)}`,
              { headers: getAuthHeaders() },
            );
            const accessResult = await accessResponse.json().catch(() => null);
            if (
              accessResponse.ok &&
              accessResult?.data?.accessible &&
              accessResult.data.accessUrl
            ) {
              asset.url = accessResult.data.accessUrl;
              if (asset.type.startsWith("image/"))
                asset.thumbnailUrl = accessResult.data.accessUrl;
            } else if (asset.storageLocation === "glacier") {
              asset.url = "";
              asset.thumbnailUrl = undefined;
            }
          } catch (error) {
            console.error(`Failed to load preview for ${asset.name}:`, error);
          }
        }),
      );

      setAssets((currentAssets) => {
        if (seq !== loadSeqRef.current) return currentAssets;
        const localByUuid = new Map(
          currentAssets.map((asset) => [asset.uuid || asset.id, asset]),
        );
        const merged = databaseAssets.map((databaseAsset) => ({
          ...localByUuid.get(databaseAsset.uuid || databaseAsset.id),
          ...databaseAsset,
        }));
        saveAssetsToStorage(merged, invalidAssets);
        return merged;
      });
    } catch (error) {
      console.error("Failed to load database assets:", error);
    } finally {
      if (seq === loadSeqRef.current) setAssetsLoading(false);
    }
  };

  const loadAssetsFromStorage = (): void => {
    const savedAssets = localStorage.getItem("uploadedAssets");
    const savedInvalidAssets = localStorage.getItem("invalidAssets");

    if (savedAssets) {
      const parsed = JSON.parse(savedAssets);
      setAssets(
        parsed.map((a: any) => ({
          ...a,
          uploadDate: new Date(a.uploadDate),
          retentionEndDate: new Date(a.retentionEndDate),
          extractedMetadata: a.extractedMetadata
            ? {
                ...a.extractedMetadata,
                lastModified: a.extractedMetadata.lastModified
                  ? new Date(a.extractedMetadata.lastModified)
                  : undefined,
                extractionDate: a.extractedMetadata.extractionDate
                  ? new Date(a.extractedMetadata.extractionDate)
                  : undefined,
              }
            : undefined,
          assetIdentity: a.assetIdentity
            ? {
                ...a.assetIdentity,
                firstUploadDate: a.assetIdentity.firstUploadDate
                  ? new Date(a.assetIdentity.firstUploadDate).toISOString()
                  : new Date().toISOString(),
                versionHistory: a.assetIdentity.versionHistory || [],
                approvalHistory: a.assetIdentity.approvalHistory || [],
              }
            : undefined,
        })),
      );
    }

    if (savedInvalidAssets) {
      const parsed = JSON.parse(savedInvalidAssets);
      setInvalidAssets(
        parsed.map((a: any) => ({
          ...a,
          uploadDate: new Date(a.uploadDate),
          retentionEndDate: new Date(a.retentionEndDate),
          extractedMetadata: a.extractedMetadata
            ? {
                ...a.extractedMetadata,
                lastModified: a.extractedMetadata.lastModified
                  ? new Date(a.extractedMetadata.lastModified)
                  : undefined,
                extractionDate: a.extractedMetadata.extractionDate
                  ? new Date(a.extractedMetadata.extractionDate)
                  : undefined,
              }
            : undefined,
        })),
      );
    }
  };

  const saveAssetsToStorage = (
    validAssets: UploadedAsset[],
    invalid: UploadedAsset[],
  ): void => {
    localStorage.setItem("uploadedAssets", JSON.stringify(validAssets));
    localStorage.setItem("invalidAssets", JSON.stringify(invalid));
  };

  const loadUploadLogs = (): void => {
    const savedLogs = localStorage.getItem("uploadAttemptLogs");
    if (savedLogs) {
      setUploadAttemptLogs(JSON.parse(savedLogs));
    }
  };

  const saveUploadLog = (logEntry: any): void => {
    const updatedLogs = [...uploadAttemptLogs, logEntry];
    setUploadAttemptLogs(updatedLogs);
    localStorage.setItem("uploadAttemptLogs", JSON.stringify(updatedLogs));
  };

  // ============================================================
  // HELPER FUNCTIONS
  // ============================================================

  const generateChecksum = async (file: File): Promise<string> => {
    const buffer = await file.arrayBuffer();
    const hashBuffer = await crypto.subtle.digest("SHA-256", buffer);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map((b) => b.toString(16).padStart(2, "0")).join("");
  };

  const generateUUID = (): string => {
    return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(
      /[xy]/g,
      function (c) {
        const r = (Math.random() * 16) | 0;
        const v = c === "x" ? r : (r & 0x3) | 0x8;
        return v.toString(16);
      },
    );
  };

  const getVideoDuration = async (file: File): Promise<number> => {
    return new Promise((resolve) => {
      const video = document.createElement("video");
      const url = URL.createObjectURL(file);

      video.onloadedmetadata = () => {
        URL.revokeObjectURL(url);
        resolve(video.duration || 0);
      };

      video.onerror = () => {
        URL.revokeObjectURL(url);
        resolve(0);
      };

      video.src = url;
    });
  };

  // ============================================================
  // ASSET IDENTITY FUNCTIONS (from Aditya branch)
  // ============================================================

  const findExistingAssetByChecksum = (
    checksum: string,
  ): UploadedAsset | undefined => {
    return assets.find((a) => a.checksum === checksum);
  };

  const hasHistoricalLinkages = (asset: UploadedAsset): boolean => {
    const hasEvents = asset.linkedEvents && asset.linkedEvents.length > 0;
    const hasProducts = asset.linkedProducts && asset.linkedProducts.length > 0;
    return !!(hasEvents || hasProducts);
  };

  const createAssetIdentity = (
    checksum: string,
    fileName: string,
    version: number = 1,
  ): AssetIdentity => {
    return {
      uuid: generateUUID(),
      checksum,
      originalName: fileName,
      firstUploadDate: new Date().toISOString(),
      currentVersion: version,
      versionHistory: [],
      linkedEvents: [],
      linkedProducts: [],
      isActive: true,
      approvalStatus: "pending",
      approvalHistory: [],
    };
  };

  // ============================================================
  // METADATA EXTRACTION (from Aditya branch)
  // ============================================================

  const extractExifData = (file: File): Promise<Record<string, any>> => {
    return new Promise((resolve) => {
      setTimeout(() => {
        const exifData: Record<string, any> = {
          "Image Width": "1920",
          "Image Height": "1080",
          "Color Space": "sRGB",
          "Bits Per Pixel": "24",
          Compression: "JPEG",
          "X Resolution": "72",
          "Y Resolution": "72",
          "Resolution Unit": "inches",
          "Date Time Original": new Date().toISOString(),
          "Date Time Digitized": new Date().toISOString(),
        };

        if (file.type.startsWith("video/")) {
          exifData["Duration"] = "120 seconds";
          exifData["Frame Rate"] = "30 fps";
          exifData["Video Codec"] = "H.264";
          exifData["Audio Codec"] = "AAC";
          exifData["Bitrate"] = "5 Mbps";
        }

        resolve(exifData);
      }, 300);
    });
  };

  const extractIptcData = (file: File): Promise<Record<string, any>> => {
    return new Promise((resolve) => {
      setTimeout(() => {
        const iptcData: Record<string, any> = {
          Keywords: ["campaign", "asset", "marketing"],
          Credit: "System User",
          Caption: file.name,
          Headline: file.name,
          "Special Instructions": "Standard campaign asset",
        };
        resolve(iptcData);
      }, 200);
    });
  };

  const extractXmpData = (file: File): Promise<Record<string, any>> => {
    return new Promise((resolve) => {
      setTimeout(() => {
        const xmpData: Record<string, any> = {
          "Creator Tool": "Asset Upload System",
          "Create Date": new Date().toISOString(),
          "Modify Date": new Date().toISOString(),
          "Metadata Date": new Date().toISOString(),
          Rights: "© Campaign Assets",
        };
        resolve(xmpData);
      }, 200);
    });
  };

  const extractImageMetadata = (
    file: File,
  ): Promise<Partial<ExtractedMetadata>> => {
    return new Promise((resolve) => {
      const img = new Image();
      const url = URL.createObjectURL(file);

      img.onload = () => {
        URL.revokeObjectURL(url);
        resolve({
          imageWidth: img.width,
          imageHeight: img.height,
          imageColorSpace: "sRGB",
          imageBitsPerPixel: 24,
          imageCompression: "JPEG",
        });
      };

      img.onerror = () => {
        URL.revokeObjectURL(url);
        resolve({
          imageWidth: 0,
          imageHeight: 0,
          imageColorSpace: "Unknown",
          imageBitsPerPixel: 0,
          imageCompression: "Unknown",
        });
      };

      img.src = url;
    });
  };

  const extractVideoMetadata = async (
    file: File,
  ): Promise<Partial<ExtractedMetadata>> => {
    return new Promise((resolve) => {
      const video = document.createElement("video");
      const url = URL.createObjectURL(file);

      video.onloadedmetadata = () => {
        URL.revokeObjectURL(url);
        const aspectRatio =
          video.videoWidth && video.videoHeight
            ? (video.videoWidth / video.videoHeight).toFixed(2)
            : "Unknown";

        resolve({
          videoDuration: video.duration,
          videoWidth: video.videoWidth,
          videoHeight: video.videoHeight,
          videoFrameRate: 30,
          videoBitrate: 5000,
          videoCodec: "H.264",
          videoAspectRatio: aspectRatio,
        });
      };

      video.onerror = () => {
        URL.revokeObjectURL(url);
        resolve({
          videoDuration: 0,
          videoWidth: 0,
          videoHeight: 0,
          videoFrameRate: 0,
          videoBitrate: 0,
          videoCodec: "Unknown",
          videoAspectRatio: "Unknown",
        });
      };

      video.src = url;
    });
  };

  const extractAudioMetadata = async (
    file: File,
  ): Promise<Partial<ExtractedMetadata>> => {
    return new Promise((resolve) => {
      const audio = document.createElement("audio");
      const url = URL.createObjectURL(file);

      audio.onloadedmetadata = () => {
        URL.revokeObjectURL(url);
        resolve({
          audioDuration: audio.duration,
          audioBitrate: 128,
          audioSampleRate: 44100,
          audioChannels: 2,
          audioCodec: file.type.split("/")[1].toUpperCase() || "Unknown",
        });
      };

      audio.onerror = () => {
        URL.revokeObjectURL(url);
        resolve({
          audioDuration: 0,
          audioBitrate: 0,
          audioSampleRate: 0,
          audioChannels: 0,
          audioCodec: "Unknown",
        });
      };

      audio.src = url;
    });
  };

  const extractDocumentMetadata = (
    file: File,
  ): Promise<Partial<ExtractedMetadata>> => {
    return new Promise((resolve) => {
      setTimeout(() => {
        const metadata: Partial<ExtractedMetadata> = {
          documentPages: Math.floor(Math.random() * 20) + 1,
          documentAuthor: "System User",
          documentCreator: "Asset Upload System",
          documentProducer: file.type.includes("pdf")
            ? "PDF Generator"
            : "Document Processor",
          documentCreationDate: new Date().toISOString(),
          documentModificationDate: new Date().toISOString(),
        };
        resolve(metadata);
      }, 300);
    });
  };

  const extractMetadataFromFile = async (
    file: File,
  ): Promise<ExtractedMetadata> => {
    const errors: string[] = [];
    const extractedData: Partial<ExtractedMetadata> = {
      fileName: file.name,
      fileSize: file.size,
      fileType: file.type || "unknown",
      mimeType: file.type || "application/octet-stream",
      fileExtension: file.name.split(".").pop()?.toLowerCase() || "",
      lastModified: new Date(file.lastModified),
      extractionDate: new Date().toISOString(),
    };

    let exifData: Record<string, any> = {};
    let iptcData: Record<string, any> = {};
    let xmpData: Record<string, any> = {};

    try {
      exifData = await extractExifData(file);
      extractedData.exifData = exifData;
    } catch (error) {
      errors.push("Failed to extract EXIF metadata");
      extractedData.exifData = {};
    }

    try {
      iptcData = await extractIptcData(file);
      extractedData.iptcData = iptcData;
    } catch (error) {
      errors.push("Failed to extract IPTC metadata");
      extractedData.iptcData = {};
    }

    try {
      xmpData = await extractXmpData(file);
      extractedData.xmpData = xmpData;
    } catch (error) {
      errors.push("Failed to extract XMP metadata");
      extractedData.xmpData = {};
    }

    try {
      if (file.type.startsWith("image/")) {
        const imageData = await extractImageMetadata(file);
        Object.assign(extractedData, imageData);
      } else if (file.type.startsWith("video/")) {
        const videoData = await extractVideoMetadata(file);
        Object.assign(extractedData, videoData);
      } else if (file.type.startsWith("audio/")) {
        const audioData = await extractAudioMetadata(file);
        Object.assign(extractedData, audioData);
      } else if (
        file.type.includes("pdf") ||
        file.type.includes("word") ||
        file.type.includes("document") ||
        file.type.includes("excel") ||
        file.type.includes("sheet")
      ) {
        const docData = await extractDocumentMetadata(file);
        Object.assign(extractedData, docData);
      }
    } catch (error) {
      errors.push("Failed to extract format-specific metadata");
    }

    extractedData.embeddedMetadata = {
      ...exifData,
      ...iptcData,
      ...xmpData,
    };

    const missingFields: string[] = [];

    if (!extractedData.fileName) missingFields.push("fileName");
    if (!extractedData.fileSize) missingFields.push("fileSize");
    if (!extractedData.fileType) missingFields.push("fileType");
    if (!extractedData.mimeType) missingFields.push("mimeType");

    if (file.type.startsWith("image/")) {
      if (!extractedData.imageWidth) missingFields.push("imageWidth");
      if (!extractedData.imageHeight) missingFields.push("imageHeight");
    } else if (file.type.startsWith("video/")) {
      if (!extractedData.videoDuration) missingFields.push("videoDuration");
      if (!extractedData.videoCodec) missingFields.push("videoCodec");
    } else if (file.type.startsWith("audio/")) {
      if (!extractedData.audioCodec) missingFields.push("audioCodec");
      if (!extractedData.audioBitrate) missingFields.push("audioBitrate");
    } else if (file.type.includes("pdf") || file.type.includes("document")) {
      if (!extractedData.documentPages) missingFields.push("documentPages");
      if (!extractedData.documentAuthor) missingFields.push("documentAuthor");
    }

    const extractionStatus: "complete" | "partial" | "failed" =
      errors.length === 0 && missingFields.length === 0
        ? "complete"
        : errors.length > 0 && missingFields.length > 0
          ? "failed"
          : "partial";

    return {
      ...(extractedData as ExtractedMetadata),
      extractionStatus,
      extractionErrors: errors.length > 0 ? errors : undefined,
      missingFields: missingFields.length > 0 ? missingFields : undefined,
      extractionDate: new Date().toISOString(),
    };
  };

  const extractCodecDetails = async (file: File): Promise<string> => {
    try {
      if (file.type.startsWith("image/")) {
        return new Promise((resolve) => {
          const img = new Image();
          const url = URL.createObjectURL(file);

          img.onload = () => {
            URL.revokeObjectURL(url);
            const format = file.type.split("/")[1].toUpperCase();
            resolve(
              `Image: ${img.width}x${img.height} pixels, ${format}, ${(file.size / 1024).toFixed(0)}KB`,
            );
          };

          img.onerror = () => {
            URL.revokeObjectURL(url);
            resolve(
              `Image: ${file.type.split("/")[1].toUpperCase()}, ${(file.size / 1024).toFixed(0)}KB`,
            );
          };

          img.src = url;
        });
      }

      if (file.type.startsWith("video/")) {
        return new Promise((resolve) => {
          const video = document.createElement("video");
          const url = URL.createObjectURL(file);

          video.onloadedmetadata = () => {
            URL.revokeObjectURL(url);
            const format = file.type.split("/")[1].toUpperCase();
            resolve(
              `Video: ${video.videoWidth}x${video.videoHeight}, ${Math.round(video.duration)}s, ${format}, ${(file.size / 1024).toFixed(0)}KB`,
            );
          };

          video.onerror = () => {
            URL.revokeObjectURL(url);
            resolve(
              `Video: ${file.type.split("/")[1].toUpperCase()}, ${(file.size / 1024).toFixed(0)}KB`,
            );
          };

          video.src = url;
        });
      }

      if (file.type.startsWith("audio/")) {
        return new Promise((resolve) => {
          const audio = document.createElement("audio");
          const url = URL.createObjectURL(file);

          audio.onloadedmetadata = () => {
            URL.revokeObjectURL(url);
            const format = file.type.split("/")[1].toUpperCase();
            resolve(
              `Audio: ${Math.round(audio.duration)}s, ${format}, ${(file.size / 1024 / 1024).toFixed(2)}MB`,
            );
          };

          audio.onerror = () => {
            URL.revokeObjectURL(url);
            resolve(
              `Audio: ${file.type.split("/")[1].toUpperCase()}, ${(file.size / 1024 / 1024).toFixed(2)}MB`,
            );
          };

          audio.src = url;
        });
      }

      if (file.type.includes("pdf") || file.type.includes("document")) {
        return `Document: ${file.type.split("/")[1].toUpperCase()}, ${(file.size / 1024).toFixed(0)}KB`;
      }

      return `File: ${file.type || "unknown"}, ${(file.size / 1024).toFixed(0)}KB`;
    } catch (error) {
      console.error("Failed to extract codec details:", error);
      return `Unknown format, ${(file.size / 1024).toFixed(0)}KB`;
    }
  };

  const generateThumbnail = async (file: File): Promise<string | null> => {
    try {
      if (file.type.startsWith("image/")) {
        return new Promise((resolve) => {
          const reader = new FileReader();
          reader.onload = (e) => {
            resolve(e.target?.result as string);
          };
          reader.onerror = () => {
            resolve(null);
          };
          reader.readAsDataURL(file);
        });
      }

      if (file.type.startsWith("video/")) {
        return new Promise((resolve) => {
          const video = document.createElement("video");
          const canvas = document.createElement("canvas");
          const context = canvas.getContext("2d");
          const url = URL.createObjectURL(file);

          video.onloadedmetadata = () => {
            const seekTime = Math.min(1, video.duration * 0.1);
            video.currentTime = seekTime;
          };

          video.onseeked = () => {
            canvas.width = 320;
            canvas.height = 180;
            context?.drawImage(video, 0, 0, 320, 180);
            URL.revokeObjectURL(url);
            resolve(canvas.toDataURL("image/jpeg", 0.8));
          };

          video.onerror = () => {
            URL.revokeObjectURL(url);
            resolve(null);
          };

          video.src = url;
        });
      }

      if (file.type.startsWith("audio/")) {
        return "data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSI2NCIgaGVpZ2h0PSI2NCIgdmlld0JveD0iMCAwIDY0IDY0Ij48cmVjdCB3aWR0aD0iNjQiIGhlaWdodD0iNjQiIGZpbGw9IiNmZmZmZmYiLz48Y2lyY2xlIGN4PSIzMiIgY3k9IjMyIiByPSIyNCIgZmlsbD0iI2ZmMDAwMCIvPjx0ZXh0IHg9IjI4IiB5PSIzOCIgZm9udC1zaXplPSIyNCIgZm9udC1mYW1pbHk9IkFyaWFsIiBmaWxsPSIjZmZmIj7imrA8L3RleHQ+PC9zdmc+";
      }

      if (file.type.includes("pdf")) {
        return "data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSI2NCIgaGVpZ2h0PSI2NCIgdmlld0JveD0iMCAwIDY0IDY0Ij48cmVjdCB3aWR0aD0iNjQiIGhlaWdodD0iNjQiIGZpbGw9IiNmZmZmZmYiLz48dGV4dCB4PSIxMCIgeT0iNDAiIGZvbnQtc2l6ZT0iMjAiIGZvbnQtZmFtaWx5PSJBcmlhbCIgZmlsbD0iI2ZmMDAwMCI+UERGPC90ZXh0Pjwvc3ZnPg==";
      }

      if (file.type.includes("word") || file.type.includes("document")) {
        return "data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSI2NCIgaGVpZ2h0PSI2NCIgdmlld0JveD0iMCAwIDY0IDY0Ij48cmVjdCB3aWR0aD0iNjQiIGhlaWdodD0iNjQiIGZpbGw9IiNmZmZmZmYiLz48dGV4dCB4PSIxMCIgeT0iNDAiIGZvbnQtc2l6ZT0iMjAiIGZvbnQtZmFtaWx5PSJBcmlhbCIgZmlsbD0iIzAwMCI+RE9DPC90ZXh0Pjwvc3ZnPg==";
      }

      return null;
    } catch (error) {
      console.error("Failed to generate thumbnail:", error);
      return null;
    }
  };

  const buildMetadata = async (
    file: File,
    assetId: string,
    checksum: string,
    status: "valid" | "invalid" | "processing",
    uploadedBy: string,
    collectionId?: string,
    assetDescription?: string,
    thumbnailUrl?: string | null,
  ): Promise<MetadataSchema> => {
    const codecDetails = await extractCodecDetails(file);

    return {
      assetId,
      originalFileName: file.name,
      uploadDateTime: new Date().toISOString(),
      uploadedBy: uploadedBy || "system-user",
      fileType: file.type || "unknown",
      codecDetails,
      status,
      fileSize: file.size,
      checksum,
      collectionId: collectionId || undefined,
      thumbnailUrl: thumbnailUrl || undefined,
      assetDescription: assetDescription || undefined,
    };
  };

  const validateMetadata = (
    metadata: MetadataSchema,
  ): AssetValidationResult => {
    const missingFields: string[] = [];

    const mandatoryFields: (keyof MetadataSchema)[] = [
      "assetId",
      "originalFileName",
      "uploadDateTime",
      "uploadedBy",
      "fileType",
      "codecDetails",
      "status",
      "fileSize",
      "checksum",
    ];

    for (const field of mandatoryFields) {
      if (!metadata[field]) {
        missingFields.push(field);
      }
    }

    if (missingFields.length > 0) {
      return {
        isValid: false,
        errorReason: `Missing required metadata fields: ${missingFields.join(", ")}`,
        missingFields,
        suggestedAction: "Please ensure all required metadata is provided",
        validationErrors: missingFields.map(
          (f) => `Missing required field: ${f}`,
        ),
      };
    }

    if (metadata.checksum && metadata.checksum.length !== 64) {
      return {
        isValid: false,
        errorReason: "Checksum validation failed",
        suggestedAction: "Re-upload the file",
        validationErrors: ["Checksum comparison failed"],
      };
    }

    return { isValid: true };
  };

  const validateVideoCodec = async (
    file: File,
  ): Promise<AssetValidationResult> => {
    return new Promise((resolve) => {
      const video = document.createElement("video");
      const url = URL.createObjectURL(file);
      const errors: string[] = [];

      video.onloadedmetadata = () => {
        URL.revokeObjectURL(url);

        const canPlay = video.canPlayType(file.type);
        if (canPlay === "") {
          errors.push(`Unsupported video codec for ${file.type}`);
          resolve({
            isValid: false,
            errorReason: `Unsupported video codec: ${file.type}`,
            suggestedAction: "Convert to MP4 with H.264 codec",
            validationErrors: errors,
          });
        } else {
          resolve({ isValid: true });
        }
      };

      video.onerror = () => {
        URL.revokeObjectURL(url);
        errors.push("Unable to read video file - possible codec issue");
        resolve({
          isValid: false,
          errorReason: "Unable to read video file - possible codec issue",
          suggestedAction: "Convert to MP4 with H.264 codec",
          validationErrors: errors,
        });
      };

      video.src = url;
    });
  };

  const detectDuplicates = (): void => {
    const allAssets = [...assets, ...invalidAssets];
    const duplicateMap = new Map<string, UploadedAsset[]>();

    allAssets.forEach((asset) => {
      const key = `${asset.checksum}-${asset.size}`;
      if (!duplicateMap.has(key)) {
        duplicateMap.set(key, []);
      }
      duplicateMap.get(key)!.push(asset);
    });

    const duplicates: UploadedAsset[] = [];
    duplicateMap.forEach((group) => {
      if (group.length > 1) {
        const groupId = `dup-${Date.now()}-${Math.random()}`;
        group.forEach((asset, index) => {
          if (index > 0) {
            duplicates.push({
              ...asset,
              isDuplicate: true,
              duplicateGroupId: groupId,
            });
          }
        });
      }
    });

    setDuplicateCandidates(duplicates);
  };

  const calculateStorageMetrics = (): void => {
    const allAssets = [...assets, ...invalidAssets];
    const totalSize = allAssets.reduce((sum, asset) => sum + asset.size, 0);
    const tempSize = allAssets
      .filter((a) => a.storageLocation === "temp")
      .reduce((sum, a) => sum + a.size, 0);
    const longTermSize = allAssets
      .filter(
        (a) =>
          a.storageLocation === "longterm" ||
          a.storageLocation === "glacier" ||
          a.storageLocation === "restoring",
      )
      .reduce((sum, a) => sum + a.size, 0);

    const totalCapacity = 10 * 1024 * 1024 * 1024;
    const usedCapacity = catalogSummary
      ? catalogSummary.totalSize +
        invalidAssets.reduce((sum, a) => sum + a.size, 0)
      : totalSize;
    const availableCapacity = totalCapacity - usedCapacity;

    const percentageUsed = (usedCapacity / totalCapacity) * 100;

    setStorageMetrics({
      totalCapacity,
      usedCapacity,
      availableCapacity,
      tempStorageUsed: catalogSummary ? catalogSummary.tempSize : tempSize,
      longTermStorageUsed: catalogSummary
        ? catalogSummary.longtermSize +
          catalogSummary.glacierSize +
          catalogSummary.restoringSize
        : longTermSize,
      percentageUsed,
    });

    if (percentageUsed > 90) {
      console.warn("⚠️ Storage limit exceeded!");
    }
  };

  // ============================================================
  // VALIDATE ASSET (Combined from both branches)
  // ============================================================

  const validateAsset = async (
    file: File,
    collectionId?: string,
    assetDescription?: string,
  ): Promise<AssetValidationResult> => {
    const validationErrors: string[] = [];

    // Enhanced validation from both branches
    const isImage = file.type.startsWith("image/");
    const isVideo =
      file.type.startsWith("video/") ||
      /\.(mpeg|mpg|mpe|m2v)$/i.test(file.name);
    const isAudio = file.type.startsWith("audio/");
    const fileExtension =
      file.name.split(".").pop()?.toLowerCase() || "unknown";
    const isDocument = fileExtension === "pdf" || fileExtension === "docx";

    if (!isImage && !isVideo && !isAudio && !isDocument) {
      validationErrors.push(
        `Only image, video, audio, PDF, and DOCX files are allowed. Received: ${fileExtension} (${file.type || "unknown type"})`,
      );
      return {
        isValid: false,
        errorReason: `Unsupported file type: ${fileExtension}.`,
        suggestedAction:
          "Please upload an image, video, audio, PDF, or DOCX file.",
        validationErrors,
      };
    }

    // Video-specific validation
    if (isVideo) {
      const maxVideoSize = 40 * 1024 * 1024 * 1024; // 40GB from Aditya branch
      if (file.size > maxVideoSize) {
        validationErrors.push(
          `Video file size (${formatFileSize(file.size)}) exceeds limit of ${formatFileSize(maxVideoSize)}`,
        );
        return {
          isValid: false,
          errorReason: `Video file size exceeds 40GB limit`,
          suggestedAction: `Please compress the video or split into smaller files. Max size: 40GB`,
          validationErrors,
        };
      }

      try {
        const duration = await getVideoDuration(file);
        const maxDuration = 3600; // 1 hour from Aditya branch
        if (duration > maxDuration) {
          validationErrors.push(
            `Video duration (${Math.round(duration)}s) exceeds limit of ${maxDuration}s (1 hour)`,
          );
          return {
            isValid: false,
            errorReason: `Video duration (${Math.round(duration)}s) exceeds 1 hour limit`,
            suggestedAction: `Please shorten the video to under 1 hour. Current duration: ${Math.round(duration)}s`,
            validationErrors,
          };
        }
      } catch (error) {
        validationErrors.push(
          "Could not read video duration. File may be corrupted.",
        );
        return {
          isValid: false,
          errorReason: "Could not read video duration",
          suggestedAction:
            "Please check if the video file is valid and not corrupted.",
          validationErrors,
        };
      }
    } else {
      // Non-video files: 2GB limit from Aditya branch
      const maxOtherSize = 2 * 1024 * 1024 * 1024;
      if (file.size > maxOtherSize) {
        validationErrors.push(
          `File size (${formatFileSize(file.size)}) exceeds limit of ${formatFileSize(maxOtherSize)}`,
        );
        return {
          isValid: false,
          errorReason: `File size exceeds 2GB limit`,
          suggestedAction: `Please compress the file or split into smaller files. Max size: 2GB`,
          validationErrors,
        };
      }
    }

    if (isVideo) {
      try {
        const codecValidation = await validateVideoCodec(file);
        if (!codecValidation.isValid) {
          console.warn(
            `Video codec warning for ${file.name}: ${codecValidation.errorReason}`,
          );
        }
      } catch (error) {
        console.warn(`Could not validate video codec for ${file.name}:`, error);
      }
    }

    if (isAudio) {
      console.log(`Audio file detected: ${file.name} (${file.type})`);
    }

    const checksum = await generateChecksum(file);

    const existingAsset = findExistingAssetByChecksum(checksum);
    if (existingAsset) {
      if (hasHistoricalLinkages(existingAsset)) {
        validationErrors.push(
          `Duplicate asset detected with historical linkages: ${existingAsset.name}\n` +
            `This asset is linked to ${existingAsset.linkedEvents?.length || 0} event(s) and ${existingAsset.linkedProducts?.length || 0} product(s).\n` +
            `Please request a version upgrade instead of creating a duplicate.`,
        );
        return {
          isValid: false,
          errorReason: `Duplicate asset with historical linkages detected`,
          suggestedAction: "Request version upgrade through approval process",
          validationErrors,
        };
      } else {
        validationErrors.push(
          `Duplicate asset detected: ${existingAsset.name}`,
        );
        return {
          isValid: false,
          errorReason: `Duplicate asset detected: ${existingAsset.name}`,
          suggestedAction:
            "This file already exists. Use the existing version or request a version upgrade.",
          validationErrors,
        };
      }
    }

    const metadata = await buildMetadata(
      file,
      `temp-${Date.now()}`,
      checksum,
      "processing",
      "current-user-id",
      collectionId,
      assetDescription,
    );

    const metadataValidation = validateMetadata(metadata);
    if (!metadataValidation.isValid) {
      return metadataValidation;
    }

    return {
      isValid: true,
    };
  };

  // ============================================================
  // VALIDATION MODAL (from Aditya branch)
  // ============================================================

  const initializeValidationModal = (
    fileName: string,
    total: number,
    currentIndex: number,
  ): void => {
    const isVideo = fileName.match(
      /\.(mp4|mov|avi|webm|mkv|m4v|3gp|mpeg|mpg|mpe|m2v|wmv|flv)$/i,
    );
    const limitText = isVideo ? "40GB / 1 hour" : "2GB";

    const checks: ValidationCheck[] = [
      {
        id: "file_type",
        label: "📁 File Format Validation",
        status: "pending",
      },
      {
        id: "file_size",
        label: `📏 File Size Check (${limitText} limit)`,
        status: "pending",
      },
      { id: "codec", label: "🎬 Codec Details Extraction", status: "pending" },
      {
        id: "checksum",
        label: "🔐 SHA-256 Checksum Calculation",
        status: "pending",
      },
      {
        id: "duplicate_check",
        label: "🔄 Duplicate Detection",
        status: "pending",
      },
      {
        id: "metadata",
        label: "📋 Required Metadata Validation",
        status: "pending",
      },
      {
        id: "extract_metadata",
        label: "🔍 Detailed Metadata Extraction",
        status: "pending",
      },
      { id: "thumbnail", label: "🖼️ Thumbnail Generation", status: "pending" },
    ];

    if (isVideo) {
      checks.splice(3, 0, {
        id: "video_codec",
        label: "🎞️ Video Codec Validation",
        status: "pending",
      });
      checks.splice(2, 0, {
        id: "video_duration",
        label: "⏱️ Video Duration Check (1 hour max)",
        status: "pending",
      });
    }

    setModalAssetName(fileName);
    setCurrentFileIndex(currentIndex);
    setTotalFiles(total);
    setValidationChecks(checks);
    setModalOverallStatus("validating");
    setManualCloseModal(false);
    setShowValidationModal(true);
  };

  const updateValidationCheck = (
    checkId: string,
    status: ValidationCheck["status"],
    message?: string,
  ): void => {
    setValidationChecks((prev) =>
      prev.map((check) =>
        check.id === checkId
          ? { ...check, status, message, timestamp: new Date() }
          : check,
      ),
    );
  };

  // ============================================================
  // LOGGING HELPERS (from development branch)
  // ============================================================

  const logUploadDetails = (file: File, step: string, data: any = {}) => {
    console.log(`\n${"=".repeat(60)}`);
    console.log(`📋 UPLOAD LOG: ${step}`);
    console.log(`${"=".repeat(60)}`);
    console.log(`📁 File: ${file.name}`);
    console.log(`📦 Size: ${formatFileSize(file.size)} (${file.size} bytes)`);
    console.log(`📝 Type: ${file.type || "Unknown"}`);
    console.log(`🕐 Time: ${new Date().toISOString()}`);

    if (data.checksum) {
      console.log(`🔑 Checksum: ${data.checksum}`);
    }
    if (data.duration) {
      console.log(`⏱️ Duration: ${data.duration}s`);
    }
    if (data.codec) {
      console.log(`🎬 Codec: ${data.codec}`);
    }
    if (data.resolution) {
      console.log(`📐 Resolution: ${data.resolution}`);
    }
    if (data.error) {
      console.log(`❌ Error: ${data.error}`);
    }
    if (data.response) {
      console.log(`📤 Response:`, JSON.stringify(data.response, null, 2));
    }
    if (data.index !== undefined) {
      console.log(`📊 Index: ${data.index}/${data.total}`);
    }
    if (data.lastModified) {
      console.log(`📅 Last Modified: ${data.lastModified}`);
    }
    if (data.existingAsset) {
      console.log(`📄 Existing Asset: ${data.existingAsset}`);
    }
    if (data.linkages) {
      console.log(`🔗 Linkages:`, data.linkages);
    }
    if (data.assetId) {
      console.log(`🆔 Asset ID: ${data.assetId}`);
    }
    if (data.fileCategory) {
      console.log(`📂 Category: ${data.fileCategory}`);
    }
    if (data.metadata) {
      console.log(`📊 Metadata:`, data.metadata);
    }
    if (data.errors) {
      console.log(`❌ Errors:`, data.errors);
    }
    if (
      Object.keys(data).length > 0 &&
      !data.checksum &&
      !data.duration &&
      !data.codec &&
      !data.resolution &&
      !data.error &&
      !data.response &&
      data.index === undefined &&
      !data.lastModified &&
      !data.existingAsset &&
      !data.linkages &&
      !data.assetId &&
      !data.fileCategory &&
      !data.metadata &&
      !data.errors
    ) {
      console.log(`📊 Additional Data:`, data);
    }
    console.log(`${"=".repeat(60)}\n`);
  };

  const logError = (file: File, error: any, step: string) => {
    console.log(`\n${"❌".repeat(30)}`);
    console.log(`🚨 ERROR AT STEP: ${step}`);
    console.log(`${"❌".repeat(30)}`);
    console.log(`📁 File: ${file.name}`);
    console.log(`📦 Size: ${formatFileSize(file.size)}`);
    console.log(`📝 Type: ${file.type}`);
    console.log(`🕐 Time: ${new Date().toISOString()}`);

    if (error.response) {
      console.log(`📤 Response Status: ${error.response.status}`);
      console.log(
        `📤 Response Data:`,
        JSON.stringify(error.response.data, null, 2),
      );
      console.log(`📤 Response Headers:`, error.response.headers);
    }

    if (error.request) {
      console.log(`📤 Request:`, error.request);
    }

    console.log(`❌ Error Message: ${error.message}`);
    if (error.stack) {
      console.log(`📊 Error Stack:`, error.stack);
    }
    console.log(`${"❌".repeat(30)}\n`);
  };

  // ============================================================
  // S3 UPLOAD & GLACIER FUNCTIONS (from development branch)
  // ============================================================

  const logInvalidAssets = async (invalidItems: UploadedAsset[]) => {
    if (!invalidItems.length) return;

    try {
      await fetch(`${API_ROOT_URL}/v1/assets/audit/invalid-assets`, {
        method: "POST",
        headers: getAuthHeaders({ "Content-Type": "application/json" }),
        body: JSON.stringify({
          auditUser: getAuditUser(),
          orgName: "SIMB/SuperAdmin/AssetManager",
          invalidAssets: invalidItems.map((asset) => ({
            name: asset.name,
            type: asset.type,
            size: asset.size,
            reason: asset.errorReason || "Unsupported or invalid file",
          })),
        }),
      });
    } catch (error) {
      console.error("Failed to log invalid asset audit event:", error);
    }
  };

  // ============================================================
  // GLACIER FUNCTIONS (from development branch)
  // ============================================================

  const updateAsset = (assetId: string, changes: Partial<UploadedAsset>) => {
    setAssets((currentAssets) => {
      const updated = currentAssets.map((asset) =>
        asset.id === assetId ? { ...asset, ...changes } : asset,
      );
      saveAssetsToStorage(updated, invalidAssets);
      return updated;
    });
  };

  const checkRestoreStatus = async (asset: UploadedAsset): Promise<void> => {
    if (!asset.s3Key) return;

    try {
      const response = await fetch(
        `${API_ROOT_URL}/v1/assets/glacier/status?key=${encodeURIComponent(asset.s3Key)}`,
        { headers: getAuthHeaders() },
      );
      const result = await response.json().catch(() => null);
      if (!response.ok || !result?.success) {
        throw new Error(result?.message || "Failed to check restore status");
      }

      const status = result.data;
      if (status.restored && status.accessible) {
        const existingTimer = restorePollTimers.current.get(asset.id);
        if (existingTimer) clearTimeout(existingTimer);
        restorePollTimers.current.delete(asset.id);
        updateAsset(asset.id, {
          storageLocation: "glacier",
          restoreAvailableUntil: status.restoreExpiry || undefined,
          restoreDaysRemaining: status.daysRemaining ?? undefined,
          restoredAccessUrl: status.accessUrl || undefined,
          restoreEstimate: undefined,
        });
        const expiry = status.restoreExpiry
          ? new Date(status.restoreExpiry).toLocaleString()
          : "the expiry reported by AWS";
        setArchiveMessage({
          text: `${asset.name} is restored and accessible until ${expiry} (${status.daysRemaining ?? 0} days remaining).`,
          type: "success",
        });
        return;
      }

      updateAsset(asset.id, {
        storageLocation: "restoring",
        restoreEstimate: status.estimatedCompletion || asset.restoreEstimate,
      });
      const existingTimer = restorePollTimers.current.get(asset.id);
      if (existingTimer) clearTimeout(existingTimer);
      const timer = setTimeout(() => void checkRestoreStatus(asset), 60_000);
      restorePollTimers.current.set(asset.id, timer);
    } catch (error) {
      setArchiveMessage({
        text:
          error instanceof Error
            ? error.message
            : "Failed to check restore status",
        type: "error",
      });
    }
  };

  const handleMoveToGlacier = async (asset: UploadedAsset) => {
    if (!asset.s3Key) {
      setArchiveMessage({
        text: "This asset does not have an S3 key yet.",
        type: "error",
      });
      return;
    }

    setArchiveActionId(asset.id);
    setArchiveMessage(null);
    try {
      const response = await fetch(`${API_ROOT_URL}/v1/assets/glacier/move`, {
        method: "POST",
        headers: getAuthHeaders({ "Content-Type": "application/json" }),
        body: JSON.stringify({
          key: asset.s3Key,
          tenantId: asset.tenantId,
          auditUser: getAuditUser(),
        }),
      });
      const result = await response.json().catch(() => null);
      if (!response.ok || !result?.success) {
        throw new Error(result?.message || "Failed to move file to Glacier");
      }
      updateAsset(asset.id, {
        storageLocation: "glacier",
        restoreAvailableUntil: undefined,
        restoreDaysRemaining: undefined,
        restoredAccessUrl: undefined,
        restoreEstimate: undefined,
      });
      setArchiveMessage({
        text: `${asset.name} moved to Glacier storage.`,
        type: "success",
      });
    } catch (error) {
      setArchiveMessage({
        text:
          error instanceof Error
            ? error.message
            : "Failed to move file to Glacier",
        type: "error",
      });
    } finally {
      setArchiveActionId(null);
    }
  };

  const handleRestoreAsset = async (asset: UploadedAsset) => {
    if (!asset.s3Key) {
      setArchiveMessage({
        text: "This asset does not have an S3 key yet.",
        type: "error",
      });
      return;
    }

    setArchiveActionId(asset.id);
    setArchiveMessage(null);
    try {
      const response = await fetch(
        `${API_ROOT_URL}/v1/assets/glacier/restore`,
        {
          method: "POST",
          headers: getAuthHeaders({ "Content-Type": "application/json" }),
          body: JSON.stringify({
            key: asset.s3Key,
            tenantId: asset.tenantId,
            auditUser: getAuditUser(),
          }),
        },
      );
      const result = await response.json().catch(() => null);
      if (!response.ok || !result?.success) {
        throw new Error(result?.message || "Failed to request Glacier restore");
      }
      const estimate =
        result.data?.estimatedCompletion ||
        "Standard retrieval typically takes 3–5 hours; AWS does not provide an exact completion time while it is running.";
      updateAsset(asset.id, {
        storageLocation: "restoring",
        restoreEstimate: estimate,
        restoreAvailableUntil: undefined,
        restoreDaysRemaining: undefined,
        restoredAccessUrl: undefined,
      });
      setArchiveMessage({
        text: `Restore requested for ${asset.name}. ${estimate} Once restored, it will remain available for ${result.data?.restoreDays} days.`,
        type: "success",
      });
      void checkRestoreStatus(asset);
    } catch (error) {
      setArchiveMessage({
        text:
          error instanceof Error
            ? error.message
            : "Failed to request Glacier restore",
        type: "error",
      });
    } finally {
      setArchiveActionId(null);
    }
  };

  const handleRestoreByFileName = async () => {
    const fileName = retrieveFileName.trim();
    if (!uploadTenantId) {
      setArchiveMessage({
        text: "Select a tenant (in the form below) before retrieving an asset.",
        type: "error",
      });
      return;
    }
    if (!fileName) {
      setArchiveMessage({
        text: "Enter a file name to retrieve.",
        type: "error",
      });
      return;
    }

    setArchiveActionId("retrieve-by-name");
    setArchiveMessage(null);
    try {
      const response = await fetch(
        `${API_ROOT_URL}/v1/assets/glacier/restore`,
        {
          method: "POST",
          headers: getAuthHeaders({ "Content-Type": "application/json" }),
          body: JSON.stringify({
            fileName,
            tenantId: uploadTenantId,
            auditUser: getAuditUser(),
          }),
        },
      );
      const result = await response.json().catch(() => null);
      if (!response.ok || !result?.success) {
        throw new Error(result?.message || "Failed to request Glacier restore");
      }
      const matchingAsset = assets.find(
        (asset) => asset.s3Key === result.data?.key,
      );
      if (matchingAsset) {
        updateAsset(matchingAsset.id, {
          storageLocation: "restoring",
          restoreEstimate: result.data?.estimatedCompletion,
        });
        void checkRestoreStatus(matchingAsset);
      }
      setArchiveMessage({
        text: `Restore requested for ${fileName}. ${result.data?.estimatedCompletion} It will remain available for ${result.data?.restoreDays} days after completion.`,
        type: "success",
      });
    } catch (error) {
      setArchiveMessage({
        text:
          error instanceof Error
            ? error.message
            : "Failed to request Glacier restore",
        type: "error",
      });
    } finally {
      setArchiveActionId(null);
    }
  };

  // ============================================================
  // PROCESS FILES (Combined from both branches)
  // ============================================================

  const processFiles = async (files: File[]): Promise<void> => {
    if (!uploadTenantId) {
      alert("Please select a tenant before uploading assets.");
      return;
    }
    setLastValidationFiles(files);
    console.log(`\n${"🚀".repeat(30)}`);
    console.log(`📤 STARTING UPLOAD PROCESS: ${files.length} file(s)`);
    console.log(`${"🚀".repeat(30)}\n`);

    setUploading(true);
    setUploadProgress(0);

    const validAssets: UploadedAsset[] = [];
    const invalidAssetsList: UploadedAsset[] = [];

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      console.log(
        `\n📄 PROCESSING FILE ${i + 1}/${files.length}: ${file.name}`,
      );

      setUploadProgress(((i + 1) / files.length) * 100);

      // Initialize validation modal (from Aditya branch)
      initializeValidationModal(file.name, files.length, i + 1);
      await new Promise((resolve) => setTimeout(resolve, 300));

      const logEntry: UploadLogEntry = {
        timestamp: new Date().toISOString(),
        fileName: file.name,
        fileSize: file.size,
        fileType: file.type,
        attemptNumber:
          uploadAttemptLogs.filter((l: any) => l.fileName === file.name)
            .length + 1,
        status: "processing",
      };

      try {
        // ============================================================
        // UPDATE ALL CHECKS TO "RUNNING" (from Aditya branch)
        // ============================================================
        updateValidationCheck("file_type", "running");
        await new Promise((resolve) => setTimeout(resolve, 300));

        updateValidationCheck("file_size", "running");
        await new Promise((resolve) => setTimeout(resolve, 300));

        updateValidationCheck("codec", "running");
        await new Promise((resolve) => setTimeout(resolve, 300));

        updateValidationCheck("checksum", "running");
        await new Promise((resolve) => setTimeout(resolve, 300));

        updateValidationCheck("duplicate_check", "running");
        await new Promise((resolve) => setTimeout(resolve, 300));

        updateValidationCheck("extract_metadata", "running");
        await new Promise((resolve) => setTimeout(resolve, 300));

        updateValidationCheck("thumbnail", "running");
        await new Promise((resolve) => setTimeout(resolve, 300));

        updateValidationCheck("video_duration", "running");
        await new Promise((resolve) => setTimeout(resolve, 300));

        updateValidationCheck("video_codec", "running");
        await new Promise((resolve) => setTimeout(resolve, 300));

        updateValidationCheck("metadata", "running");
        await new Promise((resolve) => setTimeout(resolve, 300));

        // ============================================================
        // CALL BACKEND API (from Aditya branch)
        // ============================================================
        // Hash before starting the request. A real checksum failure must
        // prevent the file from reaching the ingestion/upload endpoint.
        const clientChecksum = await generateChecksum(file);
        if (!/^[a-f0-9]{64}$/i.test(clientChecksum)) {
          throw new Error("SHA-256 checksum calculation failed");
        }

        const formData = new FormData();
        formData.append("files", file);
        formData.append("checksum", clientChecksum);
        const tenantIdToUse = uploadTenantId;
        formData.append("tenantId", tenantIdToUse);
        formData.append("collectionId", uploadFormData.collectionId || "");
        formData.append(
          "assetDescription",
          uploadFormData.assetDescription || "",
        );
        formData.append(
          "linkedEvents",
          JSON.stringify(uploadFormData.linkedEvents),
        );
        formData.append(
          "linkedProducts",
          JSON.stringify(uploadFormData.linkedProducts),
        );
        const currentUser = getCurrentUser();
        formData.append(
          "userId",
          currentUser?.id || currentUser?.email || "super-admin",
        );
        formData.append("userEmail", currentUser?.email || "");
        formData.append("auditUser", JSON.stringify(getAuditUser()));
        formData.append("orgName", "SIMB/SuperAdmin/AssetManager");

        console.log(`🏢 Tenant ID: ${tenantIdToUse}`);
        console.log(`📡 URL: ${ASSET_INGEST_URL}`);
        console.log(
          `📁 File: ${file.name}, Size: ${formatFileSize(file.size)}, Type: ${file.type}`,
        );

        const response = await axios.post(ASSET_INGEST_URL, formData, {
          headers: {
            "Content-Type": "multipart/form-data",
            ...getAuthHeaders(),
          },
          onUploadProgress: (progressEvent) => {
            if (progressEvent.total) {
              const percentCompleted = Math.round(
                (progressEvent.loaded * 100) / progressEvent.total,
              );
              setUploadProgress(percentCompleted);
            }
          },
        });

        console.log(`✅ API Response Status: ${response.status}`);
        console.log(
          `📤 Response Data:`,
          JSON.stringify(response.data, null, 2),
        );

        const result =
          response.data?.data?.successful?.[0] ||
          response.data?.data ||
          response.data ||
          {};
        const mediaValidation = result.mediaValidation || {};
        const metadata = result.metadata || {
          video:
            mediaValidation.mediaKind === "video"
              ? {
                  codec: mediaValidation.codec,
                  duration: mediaValidation.durationSeconds,
                }
              : undefined,
          format: {
            duration: mediaValidation.durationSeconds,
            formatName: mediaValidation.container,
          },
        };
        const validation = result.validation || { isValid: true, errors: [] };

        // ============================================================
        // UPDATE ALL CHECKS WITH RESULTS (from Aditya branch)
        // ============================================================

        updateValidationCheck(
          "file_type",
          "success",
          `✓ ${file.type || "Unknown"}`,
        );
        await new Promise((resolve) => setTimeout(resolve, 200));

        updateValidationCheck(
          "file_size",
          "success",
          `✓ ${formatFileSize(file.size)} (within limit)`,
        );
        await new Promise((resolve) => setTimeout(resolve, 200));

        const codec =
          metadata.video?.codec ||
          metadata.audio?.codec ||
          metadata.codecDetails ||
          "Unknown";
        updateValidationCheck("codec", "success", `✓ ${codec}`);
        await new Promise((resolve) => setTimeout(resolve, 200));

        const checksum = metadata.checksum || result.checksum || clientChecksum;
        if (checksum) {
          updateValidationCheck(
            "checksum",
            "success",
            `✓ ${checksum.substring(0, 16)}... (SHA-256)`,
          );
        } else {
          updateValidationCheck(
            "checksum",
            "error",
            "✗ SHA-256 checksum validation failed",
          );
        }
        await new Promise((resolve) => setTimeout(resolve, 200));

        if (result.duplicateInfo?.isDuplicate) {
          updateValidationCheck(
            "duplicate_check",
            "warning",
            `⚠️ Duplicate asset found`,
          );
        } else {
          updateValidationCheck(
            "duplicate_check",
            "success",
            "✓ No duplicate detected",
          );
        }
        await new Promise((resolve) => setTimeout(resolve, 200));

        if (metadata && Object.keys(metadata).length > 0) {
          const fieldCount = Object.keys(metadata).length;
          updateValidationCheck(
            "extract_metadata",
            "success",
            `✓ Complete metadata extracted (${fieldCount} fields)`,
          );
        } else {
          updateValidationCheck(
            "extract_metadata",
            "warning",
            "⚠️ Partial metadata extraction",
          );
        }
        await new Promise((resolve) => setTimeout(resolve, 200));

        if (result.thumbnail || metadata.thumbnailUrl) {
          updateValidationCheck(
            "thumbnail",
            "success",
            "✓ Thumbnail generated",
          );
        } else {
          updateValidationCheck(
            "thumbnail",
            "warning",
            "⚠️ Could not generate thumbnail (non-critical)",
          );
        }
        await new Promise((resolve) => setTimeout(resolve, 200));

        const duration =
          metadata.format?.duration ||
          metadata.video?.duration ||
          metadata.duration;
        if (duration && duration > 0) {
          updateValidationCheck(
            "video_duration",
            "success",
            `✓ ${Math.round(duration)}s (within 1 hour limit)`,
          );
        } else {
          updateValidationCheck(
            "video_duration",
            "warning",
            "⚠️ Could not read video duration",
          );
        }
        await new Promise((resolve) => setTimeout(resolve, 200));

        if (metadata.video) {
          updateValidationCheck(
            "video_codec",
            "success",
            `✓ ${metadata.video.codec || "Unknown"} supported`,
          );
        } else {
          updateValidationCheck(
            "video_codec",
            "warning",
            "⚠️ Video codec validation skipped (not a video)",
          );
        }
        await new Promise((resolve) => setTimeout(resolve, 200));

        // ============================================================
        // PROCESS VALIDATION RESULT (Combined)
        // ============================================================

        const isValid = validation.isValid !== false;
        const errors = validation.errors || [];

        if (isValid) {
          if (result.duplicate) {
            updateValidationCheck(
              "duplicate_check",
              "warning",
              `⚠️ Duplicate detected. Existing UUID: ${result.uuid}`,
            );
            updateValidationCheck(
              "metadata",
              "success",
              "✓ Existing asset identity reused; no duplicate record created",
            );
            setModalOverallStatus("success");
            clearAssetUploadFailures(getUploadFailureKey(file));
            logEntry.status = "success";
            logEntry.assetId = result.id;
            logEntry.checksum = result.checksum || clientChecksum;
            console.log(
              `⚠️ Duplicate asset skipped: ${file.name}; existing UUID: ${result.uuid}`,
            );
            alert(
              `⚠️ Duplicate asset detected.\n\nNo new asset or UUID was created.\nExisting UUID: ${result.uuid}`,
            );
            continue;
          }

          updateValidationCheck(
            "metadata",
            "success",
            "✓ All required metadata fields present",
          );
          setModalOverallStatus("success");
          console.log(`✅ All checks passed! Asset is valid.`);

          const assetUuid = result.uuid || result.id || generateUUID();
          const assetId = result.id || `asset-${Date.now()}`;

          // Create asset object with COMPLETE metadata
          const asset: UploadedAsset = {
            id: assetId,
            uuid: assetUuid,
            name: file.name,
            formattedName: result.namingFormat,
            type: file.type,
            size: file.size,
            url: result.url || URL.createObjectURL(file),
            uploadDate: new Date(result.createdAt || result.uploadDate),
            status: "valid",
            file,
            retentionEndDate: new Date(result.retentionEnd),
            storageLocation: "temp",
            checksum: checksum || "",
            metadata: {
              assetId: assetId,
              originalFileName: file.name,
              uploadDateTime: new Date().toISOString(),
              uploadedBy: "system-user",
              fileType: file.type || "unknown",
              codecDetails: codec || "unknown",
              status: "valid",
              fileSize: file.size,
              checksum: checksum || "",
              collectionId: uploadFormData.collectionId || undefined,
              assetDescription: uploadFormData.assetDescription || undefined,
              thumbnailUrl: result.thumbnail || undefined,
            },
            extractedMetadata: {
              fileName: file.name,
              fileSize: file.size,
              fileType: file.type,
              mimeType: file.type,
              fileExtension: file.name.split(".").pop()?.toLowerCase() || "",
              lastModified: new Date(file.lastModified),
              imageWidth: metadata.image?.width || metadata.imageWidth,
              imageHeight: metadata.image?.height || metadata.imageHeight,
              imageColorSpace:
                metadata.image?.colorSpace || metadata.imageColorSpace,
              imageBitsPerPixel:
                metadata.image?.bitsPerPixel || metadata.imageBitsPerPixel,
              imageCompression:
                metadata.image?.compression || metadata.imageCompression,
              videoDuration:
                metadata.video?.duration ||
                metadata.videoDuration ||
                metadata.format?.duration,
              videoWidth: metadata.video?.width || metadata.videoWidth,
              videoHeight: metadata.video?.height || metadata.videoHeight,
              videoFrameRate:
                metadata.video?.frameRate || metadata.videoFrameRate,
              videoBitrate: metadata.video?.bitrate || metadata.videoBitrate,
              videoCodec: metadata.video?.codec || metadata.videoCodec,
              videoAspectRatio:
                metadata.video?.aspectRatio || metadata.videoAspectRatio,
              audioDuration: metadata.audio?.duration || metadata.audioDuration,
              audioBitrate: metadata.audio?.bitrate || metadata.audioBitrate,
              audioSampleRate:
                metadata.audio?.sampleRate || metadata.audioSampleRate,
              audioChannels: metadata.audio?.channels || metadata.audioChannels,
              audioCodec: metadata.audio?.codec || metadata.audioCodec,
              documentPages: metadata.document?.pages || metadata.documentPages,
              documentAuthor:
                metadata.document?.author || metadata.documentAuthor,
              documentCreator:
                metadata.document?.creator || metadata.documentCreator,
              documentProducer:
                metadata.document?.producer || metadata.documentProducer,
              documentCreationDate:
                metadata.document?.creationDate ||
                metadata.documentCreationDate,
              documentModificationDate:
                metadata.document?.modificationDate ||
                metadata.documentModificationDate,
              embeddedMetadata: metadata.embeddedMetadata || {},
              exifData: metadata.exif || metadata.exifData || {},
              iptcData: metadata.iptc || metadata.iptcData || {},
              xmpData: metadata.xmp || metadata.xmpData || {},
              formatName: metadata.format?.formatName || metadata.formatName,
              formatLongName:
                metadata.format?.formatLong || metadata.formatLong,
              formatBitrate: metadata.format?.bitrate || metadata.formatBitrate,
              extractionStatus: metadata.extractionStatus || "complete",
              extractionDate:
                metadata.extractionDate || new Date().toISOString(),
              extractionErrors: metadata.extractionErrors || [],
              missingFields: metadata.missingFields || [],
            },
            version: result.version || 1,
            thumbnailUrl: result.thumbnail || undefined,
            collectionId: uploadFormData.collectionId,
            assetDescription: uploadFormData.assetDescription,
            assetIdentity: {
              uuid: assetUuid,
              checksum: checksum || "",
              originalName: file.name,
              firstUploadDate: new Date().toISOString(),
              currentVersion: result.version || 1,
              versionHistory: result.versionHistory || [],
              linkedEvents: uploadFormData.linkedEvents || [],
              linkedProducts: uploadFormData.linkedProducts || [],
              isActive: true,
              approvalStatus: result.approvalStatus || "pending",
              approvalHistory: result.approvalHistory || [],
            },
            linkedEvents: uploadFormData.linkedEvents || [],
            linkedProducts: uploadFormData.linkedProducts || [],
            approvalStatus: result.approvalStatus || "pending",
            isLatestVersion: true,
            s3Key: result.s3Key || result.key,
            tenantId: uploadTenantId,
            tenantName: tenants.find((t) => t.id === uploadTenantId)?.name,
            etag: result.etag,
          };

          validAssets.push(asset);
          logEntry.status = "success";
          logEntry.assetId = assetId;
          logEntry.checksum = checksum;

          console.log(`✅ Asset uploaded successfully: ${file.name}`);
          console.log(`📊 Asset UUID: ${assetUuid}`);
          console.log(`📊 Asset Version: ${result.version || 1}`);
          console.log(
            `📊 Approval Status: ${result.approvalStatus || "pending"}`,
          );
        } else {
          const errorMessages =
            errors.length > 0 ? errors : ["Validation failed"];
          updateValidationCheck(
            "metadata",
            "error",
            `✗ ${errorMessages.join(", ")}`,
          );
          setModalOverallStatus("error");

          console.log(
            `❌ Asset validation failed: ${errorMessages.join(", ")}`,
          );

          const asset: UploadedAsset = {
            id: `asset-${Date.now()}`,
            uuid: generateUUID(),
            name: file.name,
            type: file.type,
            size: file.size,
            url: URL.createObjectURL(file),
            uploadDate: new Date(),
            status: "invalid",
            errorReason: errorMessages.join(", "),
            validationErrors: errorMessages,
            file,
            retentionEndDate: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000),
            storageLocation: "temp",
            version: 1,
            collectionId: uploadFormData.collectionId,
            assetDescription: uploadFormData.assetDescription,
            linkedEvents: uploadFormData.linkedEvents || [],
            linkedProducts: uploadFormData.linkedProducts || [],
            approvalStatus: "pending",
            isLatestVersion: true,
          };

          invalidAssetsList.push(asset);
          logEntry.status = "failed";
          logEntry.errorReason = errorMessages.join(", ");
        }
      } catch (error: any) {
        console.error(`❌ Error uploading ${file.name}:`, error);

        setModalOverallStatus("error");
        const errorMessage =
          error.response?.data?.message || error.message || "Upload failed";
        updateValidationCheck("metadata", "error", `✗ ${errorMessage}`);

        const asset: UploadedAsset = {
          id: `asset-${Date.now()}`,
          uuid: generateUUID(),
          name: file.name,
          type: file.type,
          size: file.size,
          url: URL.createObjectURL(file),
          uploadDate: new Date(),
          status: "invalid",
          errorReason: errorMessage,
          validationErrors: [errorMessage],
          file,
          retentionEndDate: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000),
          storageLocation: "temp",
          version: 1,
          collectionId: uploadFormData.collectionId,
          assetDescription: uploadFormData.assetDescription,
          linkedEvents: uploadFormData.linkedEvents || [],
          linkedProducts: uploadFormData.linkedProducts || [],
          approvalStatus: "pending",
          isLatestVersion: true,
        };

        invalidAssetsList.push(asset);
        logEntry.status = "failed";
        logEntry.errorReason = errorMessage;
      }

      saveUploadLog(logEntry);
      await new Promise((resolve) => setTimeout(resolve, 1500));
    }

    // ============================================================
    // LOG INVALID ASSETS (valid files were uploaded by the ingestion endpoint above)
    // ============================================================

    invalidAssetsList.forEach((asset) => {
      recordAssetUploadFailure(getUploadFailureKey(asset.file), {
        fileName: asset.name,
        assetId: asset.parentAssetId,
        trackingId: asset.id,
        error:
          asset.errorReason ||
          asset.validationErrors?.join(", ") ||
          "Asset upload or validation failed",
        source: "Super Admin Asset Upload",
      });
    });

    await logInvalidAssets(invalidAssetsList);

    // ============================================================
    // UPDATE STATE
    // ============================================================

    const newAssets = [...assets, ...validAssets];
    const newInvalidAssets = [
      ...invalidAssets,
      ...invalidAssetsList.map((a) => ({ ...a, tenantId: uploadTenantId })),
    ];

    setAssets(newAssets);
    setInvalidAssets(newInvalidAssets);
    saveAssetsToStorage(newAssets, newInvalidAssets);
    calculateStorageMetrics();
    detectDuplicates();

    setUploading(false);
    filterTenantIdRef.current = uploadTenantId;
    setFilterTenantId(uploadTenantId);
    currentPageRef.current = 1;
    setCurrentPage(1);
    void loadAssetsFromDatabase();

    setUploadFormData({
      collectionId: "",
      assetDescription: "",
      linkedEvents: [],
      linkedProducts: [],
    });

    const totalValid = validAssets.length;
    const totalInvalid = invalidAssetsList.length;

    console.log(`\n${"📊".repeat(30)}`);
    console.log(`📊 FINAL UPLOAD SUMMARY`);
    console.log(`${"📊".repeat(30)}`);
    console.log(`✅ Successfully uploaded: ${totalValid} file(s)`);
    console.log(`❌ Failed: ${totalInvalid} file(s)`);
    console.log(`${"📊".repeat(30)}\n`);

    if (totalInvalid > 0) {
      const errorMessages = invalidAssetsList
        .map((a) => `• ${a.name}: ${a.errorReason || "Validation failed"}`)
        .join("\n");

      alert(
        `✅ ${totalValid} assets uploaded successfully.\n` +
          `❌ ${totalInvalid} assets were rejected.\n\n` +
          `Rejected assets:\n${errorMessages}\n\n` +
          `Check the "Invalid Assets" tab for details.`,
      );
    } else if (totalValid > 0) {
      alert(`✅ ${totalValid} assets uploaded successfully!`);
    }
  };

  // ============================================================
  // UI EVENT HANDLERS
  // ============================================================

  const handleDragOver = (e: React.DragEvent): void => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent): void => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = async (e: React.DragEvent): Promise<void> => {
    e.preventDefault();
    setIsDragging(false);
    if (!uploadTenantId) {
      alert("Please select a tenant first.");
      return;
    }
    const files = Array.from(e.dataTransfer.files);
    await processFiles(files);
  };

  const handleFileSelect = async (
    e: React.ChangeEvent<HTMLInputElement>,
  ): Promise<void> => {
    if (!uploadTenantId) {
      alert("Please select a tenant first.");
      e.target.value = "";
      return;
    }
    const files = Array.from(e.target.files || []);
    await processFiles(files);
  };

  const checkAndMoveExpiredAssets = (): void => {
    // The backend scheduler owns retention expiry and Glacier transitions.
    // Refresh from PostgreSQL instead of simulating a storage move locally.
    void loadAssetsFromDatabase();
  };

  const handleReuploadInvalid = async (assetId: string): Promise<void> => {
    const invalidAsset = invalidAssets.find((a) => a.id === assetId);
    if (!invalidAsset) return;

    const input = document.createElement("input");
    input.type = "file";
    input.accept = getAcceptString();
    input.onchange = async (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (file) {
        const isImage = file.type.startsWith("image/");
        const isVideo =
          file.type.startsWith("video/") ||
          /\.(mpeg|mpg|mpe|m2v)$/i.test(file.name);
        const isAudio = file.type.startsWith("audio/");
        const fileExtension = file.name.split(".").pop()?.toLowerCase();
        const isDocument = fileExtension === "pdf" || fileExtension === "docx";

        if (!isImage && !isVideo && !isAudio && !isDocument) {
          alert(
            `❌ Only image, video, audio, PDF, and DOCX files are allowed. Received: ${file.name}`,
          );
          return;
        }

        if (invalidAsset.assetIdentity) {
          const existingAsset = assets.find(
            (a) => a.assetIdentity?.uuid === invalidAsset.assetIdentity?.uuid,
          );
          if (existingAsset && hasHistoricalLinkages(existingAsset)) {
            alert(
              "This asset has historical linkages. Please use the version upgrade workflow.",
            );
            return;
          }
        }

        const validation = await validateAsset(file);
        if (validation.isValid) {
          clearAssetUploadFailures(getUploadFailureKey(invalidAsset.file));
          const newAssetId = `asset-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
          const checksum = await generateChecksum(file);
          const thumbnailUrl = await generateThumbnail(file);
          const codecDetails = await extractCodecDetails(file);
          const extractedMetadata = await extractMetadataFromFile(file);

          const metadata = await buildMetadata(
            file,
            newAssetId,
            checksum,
            "valid",
            "current-user-id",
            invalidAsset.collectionId,
            invalidAsset.assetDescription,
            thumbnailUrl,
          );

          const assetIdentity =
            invalidAsset.assetIdentity ||
            createAssetIdentity(checksum, file.name);

          const newVersion: UploadedAsset = {
            ...invalidAsset,
            id: newAssetId,
            name: file.name,
            type: file.type,
            size: file.size,
            url: URL.createObjectURL(file),
            file,
            status: "valid",
            errorReason: undefined,
            validationErrors: undefined,
            uploadDate: new Date(),
            retentionEndDate: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000),
            version: (invalidAsset.version || 0) + 1,
            parentId: invalidAsset.assetIdentity?.uuid || invalidAsset.id,
            checksum: checksum,
            thumbnailUrl: thumbnailUrl || undefined,
            metadata: metadata,
            extractedMetadata: extractedMetadata,
            assetIdentity: assetIdentity,
            linkedEvents: invalidAsset.linkedEvents || [],
            linkedProducts: invalidAsset.linkedProducts || [],
            approvalStatus: "pending",
          };

          const updatedInvalid = invalidAssets.filter((a) => a.id !== assetId);
          setAssets([...assets, newVersion]);
          setInvalidAssets(updatedInvalid);
          saveAssetsToStorage([...assets, newVersion], updatedInvalid);
          calculateStorageMetrics();

          alert("✅ Asset re-uploaded successfully!");
        } else {
          const attempts = recordAssetUploadFailure(
            getUploadFailureKey(invalidAsset.file),
            {
              fileName: file.name,
              assetId: invalidAsset.parentAssetId,
              trackingId: invalidAsset.id,
              error: validation.errorReason || "Asset validation failed",
              source: "Super Admin Asset Upload",
            },
          );
          if (attempts < 3) {
            alert(
              `❌ Asset still invalid (attempt ${attempts} of 3): ${validation.errorReason}`,
            );
          }
        }
      }
    };
    input.click();
  };

  const handleReuploadAllInvalid = (): void => {
    if (invalidAssets.length === 0) return;
    alert(
      "Please upload corrected versions of the invalid assets.\nSelect multiple files if needed.",
    );
    fileInputRef.current?.click();
  };

  const handleDeleteAsset = (assetId: string, isInvalid: boolean): void => {
    const asset = isInvalid
      ? invalidAssets.find((a) => a.id === assetId)
      : assets.find((a) => a.id === assetId);

    if (!asset) return;

    let warningMessage = `⚠️ Are you sure you want to delete this asset?\n\n`;
    warningMessage += `📁 Asset: ${asset.name}\n`;
    warningMessage += `📦 Size: ${formatFileSize(asset.size)}\n`;
    warningMessage += `📝 Type: ${asset.type}\n`;
    warningMessage += `🔑 Asset ID: ${asset.assetIdentity?.uuid || asset.id}\n`;
    warningMessage += `📊 Status: ${asset.status}\n`;

    const hasLinkages =
      (asset.linkedEvents?.length || 0) > 0 ||
      (asset.linkedProducts?.length || 0) > 0;

    if (hasLinkages) {
      warningMessage += `\n⚠️ WARNING: This asset has historical linkages!\n`;
      warningMessage += `   Linked Events: ${asset.linkedEvents?.length || 0}\n`;
      warningMessage += `   Linked Products: ${asset.linkedProducts?.length || 0}\n`;
      warningMessage += `   Deleting this asset may break these linkages!\n`;
    }

    warningMessage += `\nThis action cannot be undone. Are you sure you want to continue?`;

    const confirmed = confirm(warningMessage);
    if (!confirmed) return;

    if (isInvalid) {
      const updated = invalidAssets.filter((a) => a.id !== assetId);
      setInvalidAssets(updated);
      saveAssetsToStorage(assets, updated);
    } else {
      const updated = assets.filter((a) => a.id !== assetId);
      setAssets(updated);
      saveAssetsToStorage(updated, invalidAssets);
    }
    calculateStorageMetrics();
    detectDuplicates();
  };

  const handleDebugAsset = (asset: UploadedAsset): void => {
    setSelectedAssetForDebug(asset);
  };

  const handleViewMetadata = (asset: UploadedAsset): void => {
    setSelectedAssetForMetadata(asset);
    setShowMetadataModal(true);
  };

  const handleViewVersionHistory = (asset: UploadedAsset): void => {
    setSelectedAssetForVersions(asset);
    setShowVersionHistory(true);
  };

  const handleRemoveDuplicates = (): void => {
    if (selectedDuplicates.length === 0) {
      alert("Please select duplicates to remove.");
      return;
    }

    const confirmed = confirm(
      `Are you sure you want to remove ${selectedDuplicates.length} duplicate asset(s)?\nThis action cannot be undone.`,
    );
    if (!confirmed) return;

    const updatedAssets = assets.filter(
      (a) => !selectedDuplicates.includes(a.id),
    );
    const updatedInvalid = invalidAssets.filter(
      (a) => !selectedDuplicates.includes(a.id),
    );

    setAssets(updatedAssets);
    setInvalidAssets(updatedInvalid);
    saveAssetsToStorage(updatedAssets, updatedInvalid);

    const removalLog = {
      timestamp: new Date(),
      action: "DUPLICATE_REMOVAL",
      removedAssets: selectedDuplicates.length,
      assetIds: selectedDuplicates,
    };
    localStorage.setItem("duplicateRemovalLog", JSON.stringify(removalLog));

    setSelectedDuplicates([]);
    setShowDuplicateDetection(false);
    detectDuplicates();
    calculateStorageMetrics();

    alert(
      `✅ Successfully removed ${selectedDuplicates.length} duplicate asset(s).`,
    );
  };

  // ============================================================
  // APPROVAL FUNCTIONS (from Aditya branch)
  // ============================================================

  const handleApproval = (
    assetId: string,
    action: "approve" | "reject" | "request_modification",
    comment: string,
  ): void => {
    const updatedAssets = assets.map((asset) => {
      if (asset.id !== assetId && asset.assetIdentity?.uuid !== assetId) {
        return asset;
      }

      const targetAsset =
        asset.id === assetId || asset.assetIdentity?.uuid === assetId;
      if (!targetAsset) return asset;

      const updatedAsset = { ...asset };
      if (!updatedAsset.assetIdentity) return asset;

      const approvalRecord: ApprovalRecord = {
        approvalId: `apr-${Date.now()}-${Math.random().toString(36).substr(2, 6)}`,
        timestamp: new Date().toISOString(),
        requestedBy: "current-user",
        status:
          action === "approve"
            ? "approved"
            : action === "reject"
              ? "rejected"
              : "pending",
        comments: comment,
        modificationReason: action === "request_modification" ? comment : "",
        versionNumber: updatedAsset.assetIdentity.currentVersion,
      };

      if (action === "approve") {
        updatedAsset.approvalStatus = "approved";
        updatedAsset.assetIdentity.approvalStatus = "approved";
        updatedAsset.isLatestVersion = true;
      } else if (action === "reject") {
        updatedAsset.approvalStatus = "rejected";
        updatedAsset.assetIdentity.approvalStatus = "rejected";
        updatedAsset.isLatestVersion = false;
      } else if (action === "request_modification") {
        updatedAsset.approvalStatus = "modification_requested";
        updatedAsset.assetIdentity.approvalStatus = "modification_requested";
      }

      updatedAsset.assetIdentity.approvalHistory.push(approvalRecord);
      return updatedAsset;
    });

    setAssets(updatedAssets);
    saveAssetsToStorage(updatedAssets, invalidAssets);
    setApprovalModal({ ...approvalModal, show: false, comment: "" });
  };

  // ============================================================
  // UTILITY FUNCTIONS
  // ============================================================

  const getAcceptString = (): string => {
    return "image/*,video/*,audio/*,.mpeg,.mpg,.mpe,.m2v,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,.pdf,.docx";
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  const getDaysRemaining = (retentionEndDate: Date): number => {
    const now = new Date();
    const diff = retentionEndDate.getTime() - now.getTime();
    return Math.ceil(diff / (1000 * 60 * 60 * 24));
  };

  const getAssetIcon = (type: string): string => {
    if (type.startsWith("image/")) return "🖼️";
    if (type.startsWith("video/")) return "🎬";
    if (type.startsWith("audio/")) return "🎵";
    if (type.includes("pdf")) return "📕";
    if (type.includes("word") || type.includes("document")) return "📘";
    if (type.includes("excel") || type.includes("sheet")) return "📗";
    return "📄";
  };

  const getStorageStatusColor = (percentage: number): string => {
    if (percentage > 90) return "bg-red-500";
    if (percentage > 80) return "bg-yellow-500";
    return "bg-green-500";
  };

  const getCheckStatusIcon = (
    status: ValidationCheck["status"],
  ): React.ReactElement | null => {
    switch (status) {
      case "pending":
        return (
          <span className="w-5 h-5 rounded-full border-2 border-gray-300 inline-block"></span>
        );
      case "running":
        return (
          <div className="w-5 h-5 border-2 border-blue-500 border-t-transparent rounded-full animate-spin inline-block"></div>
        );
      case "success":
        return <span className="text-green-500 text-lg">✓</span>;
      case "error":
        return <span className="text-red-500 text-lg">✗</span>;
      case "warning":
        return <span className="text-yellow-500 text-lg">⚠</span>;
      default:
        return null;
    }
  };

  const getCheckStatusColor = (status: ValidationCheck["status"]): string => {
    switch (status) {
      case "pending":
        return "border-gray-300";
      case "running":
        return "border-blue-500 bg-blue-50";
      case "success":
        return "border-green-500 bg-green-50";
      case "error":
        return "border-red-500 bg-red-50";
      case "warning":
        return "border-yellow-500 bg-yellow-50";
      default:
        return "border-gray-300";
    }
  };

  const getStorageLabel = (asset: UploadedAsset): string => {
    if (asset.storageLocation === "glacier") return "Archived in Glacier";
    if (asset.storageLocation === "restoring") return "Restore requested";
    if (asset.storageLocation === "archiving") return "Moving to Glacier";
    if (asset.storageLocation === "longterm") return "In long-term storage";
    return `${getDaysRemaining(asset.retentionEndDate)} days left in temp storage`;
  };

  // ============================================================
  // DEFAULT COMPANY ID
  // ============================================================

  // const DEFAULT_COMPANY_ID = '11111111-1111-1111-1111-111111111111';

  // ============================================================
  // RENDER FUNCTIONS (Combined)
  // ============================================================

  const renderMetadataModal = (): React.ReactElement | null => {
    if (!showMetadataModal || !selectedAssetForMetadata) return null;

    const metadata = selectedAssetForMetadata.extractedMetadata;
    const hasMetadata = metadata && Object.keys(metadata).length > 0;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
          <div className="p-6">
            <div className="flex justify-between items-center mb-6">
              <div>
                <h3 className="text-xl font-bold text-gray-800">
                  📋 Extracted Metadata
                </h3>
                <p className="text-sm text-gray-500 mt-1">
                  {selectedAssetForMetadata.name}
                </p>
                <p className="text-xs text-gray-400 mt-1">
                  Asset ID:{" "}
                  {selectedAssetForMetadata.assetIdentity?.uuid ||
                    selectedAssetForMetadata.id}
                </p>
              </div>
              <button
                onClick={() => setShowMetadataModal(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                ✕
              </button>
            </div>

            {!hasMetadata ? (
              <div className="text-center py-8 text-gray-500">
                <p>No metadata available for this asset</p>
              </div>
            ) : (
              <div className="space-y-6">
                {/* Extraction Status */}
                <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-700">
                      Extraction Status
                    </span>
                    <span
                      className={`px-3 py-1 rounded-full text-sm font-medium ${
                        metadata.extractionStatus === "complete"
                          ? "bg-green-100 text-green-800"
                          : metadata.extractionStatus === "partial"
                            ? "bg-yellow-100 text-yellow-800"
                            : "bg-red-100 text-red-800"
                      }`}
                    >
                      {metadata.extractionStatus.toUpperCase()}
                    </span>
                  </div>
                  {metadata.missingFields &&
                    metadata.missingFields.length > 0 && (
                      <div className="mt-2">
                        <p className="text-sm text-yellow-600">
                          ⚠️ Missing fields: {metadata.missingFields.join(", ")}
                        </p>
                      </div>
                    )}
                  {metadata.extractionErrors &&
                    metadata.extractionErrors.length > 0 && (
                      <div className="mt-2">
                        <p className="text-sm text-red-600">
                          ❌ Errors: {metadata.extractionErrors.join("; ")}
                        </p>
                      </div>
                    )}
                </div>

                {/* File Information */}
                <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                  <h4 className="font-semibold text-blue-800 mb-3">
                    📁 File Information
                  </h4>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
                    <div>
                      <p className="text-gray-500">File Name</p>
                      <p className="font-medium truncate">
                        {metadata.fileName}
                      </p>
                    </div>
                    <div>
                      <p className="text-gray-500">File Size</p>
                      <p className="font-medium">
                        {formatFileSize(metadata.fileSize)}
                      </p>
                    </div>
                    <div>
                      <p className="text-gray-500">File Type</p>
                      <p className="font-medium">
                        {metadata.fileType || "Unknown"}
                      </p>
                    </div>
                    <div>
                      <p className="text-gray-500">MIME Type</p>
                      <p className="font-medium">
                        {metadata.mimeType || "Unknown"}
                      </p>
                    </div>
                    <div>
                      <p className="text-gray-500">Extension</p>
                      <p className="font-medium">
                        {metadata.fileExtension || "Unknown"}
                      </p>
                    </div>
                    <div>
                      <p className="text-gray-500">Last Modified</p>
                      <p className="font-medium">
                        {metadata.lastModified
                          ? new Date(metadata.lastModified).toLocaleString()
                          : "Unknown"}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Image Metadata */}
                {metadata.imageWidth !== undefined && (
                  <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                    <h4 className="font-semibold text-purple-800 mb-3">
                      🖼️ Image Metadata
                    </h4>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
                      <div>
                        <p className="text-gray-500">Width</p>
                        <p className="font-medium">{metadata.imageWidth} px</p>
                      </div>
                      <div>
                        <p className="text-gray-500">Height</p>
                        <p className="font-medium">{metadata.imageHeight} px</p>
                      </div>
                      <div>
                        <p className="text-gray-500">Color Space</p>
                        <p className="font-medium">
                          {metadata.imageColorSpace || "Unknown"}
                        </p>
                      </div>
                      <div>
                        <p className="text-gray-500">Bits Per Pixel</p>
                        <p className="font-medium">
                          {metadata.imageBitsPerPixel || "Unknown"}
                        </p>
                      </div>
                      <div>
                        <p className="text-gray-500">Compression</p>
                        <p className="font-medium">
                          {metadata.imageCompression || "Unknown"}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Video Metadata */}
                {metadata.videoDuration !== undefined && (
                  <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                    <h4 className="font-semibold text-green-800 mb-3">
                      🎬 Video Metadata
                    </h4>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
                      <div>
                        <p className="text-gray-500">Duration</p>
                        <p className="font-medium">{metadata.videoDuration}s</p>
                      </div>
                      <div>
                        <p className="text-gray-500">Dimensions</p>
                        <p className="font-medium">
                          {metadata.videoWidth}x{metadata.videoHeight}
                        </p>
                      </div>
                      <div>
                        <p className="text-gray-500">Frame Rate</p>
                        <p className="font-medium">
                          {metadata.videoFrameRate || "Unknown"} fps
                        </p>
                      </div>
                      <div>
                        <p className="text-gray-500">Codec</p>
                        <p className="font-medium">
                          {metadata.videoCodec || "Unknown"}
                        </p>
                      </div>
                      <div>
                        <p className="text-gray-500">Bitrate</p>
                        <p className="font-medium">
                          {metadata.videoBitrate
                            ? `${metadata.videoBitrate} kbps`
                            : "Unknown"}
                        </p>
                      </div>
                      <div>
                        <p className="text-gray-500">Aspect Ratio</p>
                        <p className="font-medium">
                          {metadata.videoAspectRatio || "Unknown"}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Audio Metadata */}
                {metadata.audioBitrate !== undefined && (
                  <div className="bg-pink-50 p-4 rounded-lg border border-pink-200">
                    <h4 className="font-semibold text-pink-800 mb-3">
                      🎵 Audio Metadata
                    </h4>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
                      <div>
                        <p className="text-gray-500">Bitrate</p>
                        <p className="font-medium">
                          {metadata.audioBitrate} kbps
                        </p>
                      </div>
                      <div>
                        <p className="text-gray-500">Sample Rate</p>
                        <p className="font-medium">
                          {metadata.audioSampleRate || "Unknown"} Hz
                        </p>
                      </div>
                      <div>
                        <p className="text-gray-500">Channels</p>
                        <p className="font-medium">
                          {metadata.audioChannels || "Unknown"}
                        </p>
                      </div>
                      <div>
                        <p className="text-gray-500">Codec</p>
                        <p className="font-medium">
                          {metadata.audioCodec || "Unknown"}
                        </p>
                      </div>
                      <div>
                        <p className="text-gray-500">Duration</p>
                        <p className="font-medium">
                          {metadata.audioDuration
                            ? `${Math.round(metadata.audioDuration)}s`
                            : "Unknown"}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Document Metadata */}
                {metadata.documentPages !== undefined && (
                  <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
                    <h4 className="font-semibold text-orange-800 mb-3">
                      📄 Document Metadata
                    </h4>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
                      <div>
                        <p className="text-gray-500">Pages</p>
                        <p className="font-medium">{metadata.documentPages}</p>
                      </div>
                      <div>
                        <p className="text-gray-500">Author</p>
                        <p className="font-medium">
                          {metadata.documentAuthor || "Unknown"}
                        </p>
                      </div>
                      <div>
                        <p className="text-gray-500">Creator</p>
                        <p className="font-medium">
                          {metadata.documentCreator || "Unknown"}
                        </p>
                      </div>
                      <div>
                        <p className="text-gray-500">Producer</p>
                        <p className="font-medium">
                          {metadata.documentProducer || "Unknown"}
                        </p>
                      </div>
                      <div>
                        <p className="text-gray-500">Creation Date</p>
                        <p className="font-medium">
                          {metadata.documentCreationDate
                            ? new Date(
                                metadata.documentCreationDate,
                              ).toLocaleString()
                            : "Unknown"}
                        </p>
                      </div>
                      <div>
                        <p className="text-gray-500">Modification Date</p>
                        <p className="font-medium">
                          {metadata.documentModificationDate
                            ? new Date(
                                metadata.documentModificationDate,
                              ).toLocaleString()
                            : "Unknown"}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Embedded Metadata */}
                {metadata.embeddedMetadata &&
                  Object.keys(metadata.embeddedMetadata).length > 0 && (
                    <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                      <h4 className="font-semibold text-gray-700 mb-3">
                        🔍 Embedded Metadata
                      </h4>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-sm">
                        {Object.entries(metadata.embeddedMetadata).map(
                          ([key, value]) => (
                            <div
                              key={key}
                              className="bg-white p-2 rounded border border-gray-200"
                            >
                              <p className="text-xs text-gray-500">{key}</p>
                              <p className="text-sm font-medium truncate">
                                {String(value)}
                              </p>
                            </div>
                          ),
                        )}
                      </div>
                    </div>
                  )}

                {/* EXIF Data */}
                {metadata.exifData &&
                  Object.keys(metadata.exifData).length > 0 && (
                    <div className="bg-indigo-50 p-4 rounded-lg border border-indigo-200">
                      <h4 className="font-semibold text-indigo-800 mb-3">
                        📷 EXIF Data
                      </h4>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-sm">
                        {Object.entries(metadata.exifData).map(
                          ([key, value]) => (
                            <div
                              key={key}
                              className="bg-white p-2 rounded border border-indigo-200"
                            >
                              <p className="text-xs text-gray-500">{key}</p>
                              <p className="text-sm font-medium truncate">
                                {String(value)}
                              </p>
                            </div>
                          ),
                        )}
                      </div>
                    </div>
                  )}

                {/* IPTC Data */}
                {metadata.iptcData &&
                  Object.keys(metadata.iptcData).length > 0 && (
                    <div className="bg-pink-50 p-4 rounded-lg border border-pink-200">
                      <h4 className="font-semibold text-pink-800 mb-3">
                        🏷️ IPTC Data
                      </h4>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-sm">
                        {Object.entries(metadata.iptcData).map(
                          ([key, value]) => (
                            <div
                              key={key}
                              className="bg-white p-2 rounded border border-pink-200"
                            >
                              <p className="text-xs text-gray-500">{key}</p>
                              <p className="text-sm font-medium truncate">
                                {String(value)}
                              </p>
                            </div>
                          ),
                        )}
                      </div>
                    </div>
                  )}

                {/* XMP Data */}
                {metadata.xmpData &&
                  Object.keys(metadata.xmpData).length > 0 && (
                    <div className="bg-teal-50 p-4 rounded-lg border border-teal-200">
                      <h4 className="font-semibold text-teal-800 mb-3">
                        📝 XMP Data
                      </h4>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-sm">
                        {Object.entries(metadata.xmpData).map(
                          ([key, value]) => (
                            <div
                              key={key}
                              className="bg-white p-2 rounded border border-teal-200"
                            >
                              <p className="text-xs text-gray-500">{key}</p>
                              <p className="text-sm font-medium truncate">
                                {String(value)}
                              </p>
                            </div>
                          ),
                        )}
                      </div>
                    </div>
                  )}

                {/* Extraction Summary */}
                <div className="bg-gray-100 p-4 rounded-lg">
                  <h4 className="font-medium text-gray-700 mb-2">
                    📊 Extraction Summary
                  </h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
                    <div>
                      <p className="text-gray-500">Extraction Date</p>
                      <p className="font-medium">
                        {new Date(metadata.extractionDate).toLocaleString()}
                      </p>
                    </div>
                    <div>
                      <p className="text-gray-500">Status</p>
                      <p
                        className={`font-medium ${
                          metadata.extractionStatus === "complete"
                            ? "text-green-600"
                            : metadata.extractionStatus === "partial"
                              ? "text-yellow-600"
                              : "text-red-600"
                        }`}
                      >
                        {metadata.extractionStatus}
                      </p>
                    </div>
                    <div>
                      <p className="text-gray-500">Missing Fields</p>
                      <p className="font-medium">
                        {metadata.missingFields?.length || 0}
                      </p>
                    </div>
                    <div>
                      <p className="text-gray-500">Errors</p>
                      <p className="font-medium">
                        {metadata.extractionErrors?.length || 0}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div className="mt-6 flex justify-end">
              <button
                onClick={() => setShowMetadataModal(false)}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderVersionHistory = (): React.ReactElement | null => {
    if (!showVersionHistory || !selectedAssetForVersions) return null;

    const versions =
      selectedAssetForVersions.assetIdentity?.versionHistory || [];
    const hasVersions = versions.length > 0;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
          <div className="p-6">
            <div className="flex justify-between items-center mb-6">
              <div>
                <h3 className="text-xl font-bold text-gray-800">
                  📜 Version History
                </h3>
                <p className="text-sm text-gray-500 mt-1">
                  {selectedAssetForVersions.name}
                </p>
                <p className="text-xs text-gray-400 mt-1">
                  Asset ID:{" "}
                  {selectedAssetForVersions.assetIdentity?.uuid ||
                    selectedAssetForVersions.id}
                </p>
              </div>
              <button
                onClick={() => setShowVersionHistory(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                ✕
              </button>
            </div>

            {!hasVersions ? (
              <div className="text-center py-8 text-gray-500">
                <p>No version history available for this asset</p>
              </div>
            ) : (
              <div className="space-y-4">
                {versions.map((version, index) => (
                  <div
                    key={index}
                    className="border rounded-lg p-4 hover:shadow-md transition"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3">
                        <div
                          className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-bold ${
                            version.status === "active"
                              ? "bg-green-500"
                              : version.status === "pending_approval"
                                ? "bg-yellow-500"
                                : "bg-gray-500"
                          }`}
                        >
                          v{version.version}
                        </div>
                        <div>
                          <p className="font-medium text-gray-800">
                            Version {version.version}
                            {version.status === "active" && (
                              <span className="ml-2 text-xs bg-green-100 text-green-800 px-2 py-0.5 rounded">
                                Active
                              </span>
                            )}
                            {version.status === "pending_approval" && (
                              <span className="ml-2 text-xs bg-yellow-100 text-yellow-800 px-2 py-0.5 rounded">
                                Pending Approval
                              </span>
                            )}
                            {selectedAssetForVersions.isLatestVersion &&
                              version.version ===
                                selectedAssetForVersions.assetIdentity
                                  ?.currentVersion && (
                                <span className="ml-2 text-xs bg-blue-100 text-blue-800 px-2 py-0.5 rounded">
                                  Latest
                                </span>
                              )}
                          </p>
                          <p className="text-xs text-gray-500">
                            Uploaded:{" "}
                            {new Date(version.uploadDate).toLocaleString()} •
                            Size: {formatFileSize(version.fileSize)} • Checksum:{" "}
                            {version.checksum.substring(0, 16)}...
                          </p>
                          {version.changesDescription && (
                            <p className="text-xs text-gray-600 mt-1">
                              Changes: {version.changesDescription}
                            </p>
                          )}
                          {version.metadata.collectionId && (
                            <p className="text-xs text-purple-600">
                              Collection: {version.metadata.collectionId}
                            </p>
                          )}
                          {version.s3Key && (
                            <p
                              className="text-[10px] text-gray-400 truncate max-w-[220px]"
                              title={version.s3Key}
                            >
                              S3: {version.s3Key}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Approval History */}
            {selectedAssetForVersions.assetIdentity?.approvalHistory &&
              selectedAssetForVersions.assetIdentity.approvalHistory.length >
                0 && (
                <div className="mt-6">
                  <h4 className="font-semibold text-gray-700 mb-3">
                    📋 Approval History
                  </h4>
                  <div className="space-y-2">
                    {selectedAssetForVersions.assetIdentity.approvalHistory.map(
                      (record, index) => (
                        <div
                          key={index}
                          className="bg-gray-50 p-3 rounded-lg border border-gray-200"
                        >
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="text-sm font-medium text-gray-700">
                                Version {record.versionNumber} -{" "}
                                {record.status.toUpperCase()}
                              </p>
                              <p className="text-xs text-gray-500">
                                {new Date(record.timestamp).toLocaleString()} •
                                Requested by: {record.requestedBy}
                                {record.approvedBy &&
                                  ` • Approved by: ${record.approvedBy}`}
                              </p>
                              {record.comments && (
                                <p className="text-xs text-gray-600 mt-1">
                                  Comment: {record.comments}
                                </p>
                              )}
                              {record.modificationReason && (
                                <p className="text-xs text-gray-600">
                                  Reason: {record.modificationReason}
                                </p>
                              )}
                            </div>
                            <span
                              className={`px-2 py-1 rounded-full text-xs font-medium ${
                                record.status === "approved"
                                  ? "bg-green-100 text-green-800"
                                  : record.status === "rejected"
                                    ? "bg-red-100 text-red-800"
                                    : "bg-yellow-100 text-yellow-800"
                              }`}
                            >
                              {record.status}
                            </span>
                          </div>
                        </div>
                      ),
                    )}
                  </div>
                </div>
              )}

            {/* Linkages */}
            {selectedAssetForVersions.assetIdentity && (
              <div className="mt-6 grid grid-cols-2 gap-4">
                <div className="bg-blue-50 p-3 rounded-lg border border-blue-200">
                  <p className="text-sm text-blue-600 font-medium">
                    🔗 Linked Events
                  </p>
                  <p className="text-lg font-bold text-blue-700">
                    {selectedAssetForVersions.assetIdentity.linkedEvents.length}
                  </p>
                  {selectedAssetForVersions.assetIdentity.linkedEvents.length >
                    0 && (
                    <div className="mt-2 max-h-20 overflow-y-auto">
                      {selectedAssetForVersions.assetIdentity.linkedEvents.map(
                        (eventId, idx) => (
                          <span
                            key={idx}
                            className="inline-block text-xs bg-blue-100 text-blue-800 px-2 py-0.5 rounded mr-1 mt-1"
                          >
                            {eventId}
                          </span>
                        ),
                      )}
                    </div>
                  )}
                </div>
                <div className="bg-purple-50 p-3 rounded-lg border border-purple-200">
                  <p className="text-sm text-purple-600 font-medium">
                    🔗 Linked Products
                  </p>
                  <p className="text-lg font-bold text-purple-700">
                    {
                      selectedAssetForVersions.assetIdentity.linkedProducts
                        .length
                    }
                  </p>
                  {selectedAssetForVersions.assetIdentity.linkedProducts
                    .length > 0 && (
                    <div className="mt-2 max-h-20 overflow-y-auto">
                      {selectedAssetForVersions.assetIdentity.linkedProducts.map(
                        (productId, idx) => (
                          <span
                            key={idx}
                            className="inline-block text-xs bg-purple-100 text-purple-800 px-2 py-0.5 rounded mr-1 mt-1"
                          >
                            {productId}
                          </span>
                        ),
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}

            <div className="mt-6 flex justify-end">
              <button
                onClick={() => setShowVersionHistory(false)}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderApprovalModal = (): React.ReactElement | null => {
    if (!approvalModal.show) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl max-w-md w-full">
          <div className="p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-gray-800">
                📋 Approval Required
              </h3>
              <button
                onClick={() =>
                  setApprovalModal({ ...approvalModal, show: false })
                }
                className="text-gray-500 hover:text-gray-700"
              >
                ✕
              </button>
            </div>

            <div className="mb-4">
              <p className="text-sm text-gray-600">
                Asset:{" "}
                <span className="font-medium">{approvalModal.assetName}</span>
              </p>
              <p className="text-sm text-yellow-600 mt-2">
                ⚠️ This asset has historical linkages and requires approval
                before modification.
              </p>
              <div className="mt-2 p-3 bg-gray-50 rounded-lg">
                <p className="text-xs text-gray-500">Action requested:</p>
                <p className="text-sm font-medium text-gray-700">
                  {approvalModal.action === "approve"
                    ? "Approve modification"
                    : approvalModal.action === "reject"
                      ? "Reject modification"
                      : "Request modification"}
                </p>
              </div>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Comments{" "}
                <span className="text-gray-400 text-xs">(optional)</span>
              </label>
              <textarea
                value={approvalModal.comment}
                onChange={(e) =>
                  setApprovalModal({
                    ...approvalModal,
                    comment: e.target.value,
                  })
                }
                placeholder="Add comments about this request..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                rows={3}
              />
            </div>

            <div className="flex gap-2 justify-end">
              <button
                onClick={() =>
                  setApprovalModal({ ...approvalModal, show: false })
                }
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={() =>
                  handleApproval(
                    approvalModal.assetId,
                    approvalModal.action,
                    approvalModal.comment || "Approved",
                  )
                }
                className={`px-4 py-2 rounded-lg text-white ${
                  approvalModal.action === "approve"
                    ? "bg-green-600 hover:bg-green-700"
                    : approvalModal.action === "reject"
                      ? "bg-red-600 hover:bg-red-700"
                      : "bg-yellow-600 hover:bg-yellow-700"
                }`}
              >
                Submit{" "}
                {approvalModal.action === "approve"
                  ? "Approval"
                  : approvalModal.action === "reject"
                    ? "Rejection"
                    : "Modification Request"}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const confirmArchiveAction = async (): Promise<void> => {
    const { action, asset } = archiveConfirm;
    setArchiveConfirm({ show: false, action, asset: null });
    if (!asset) return;
    if (action === "archive") {
      await handleMoveToGlacier(asset);
    } else {
      await handleRestoreAsset(asset);
    }
  };

  const renderArchiveConfirmModal = (): React.ReactElement | null => {
    if (!archiveConfirm.show || !archiveConfirm.asset) return null;
    const asset = archiveConfirm.asset;
    const isArchive = archiveConfirm.action === "archive";
    const showImage =
      Boolean(asset.thumbnailUrl) && asset.type.startsWith("image/");

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 z-[60] flex items-center justify-center p-4">
        <div className="bg-white rounded-xl max-w-md w-full shadow-2xl">
          <div className="p-6">
            <h3 className="text-lg font-bold text-gray-800">
              {isArchive
                ? "❄️ Move asset to Archive?"
                : "🔄 Restore asset from Archive?"}
            </h3>
            <p className="text-sm text-gray-600 mt-1 mb-4">
              {isArchive
                ? "You are about to move this asset to Glacier archive storage. It will not be viewable until it is restored."
                : "You are about to request a restore of this asset from Glacier. Retrieval can take several hours."}
            </p>

            <div className="flex items-center gap-4 p-3 bg-gray-50 border border-gray-200 rounded-lg">
              {showImage ? (
                <img
                  src={asset.thumbnailUrl}
                  alt={asset.name}
                  className="w-20 h-20 object-cover rounded border border-gray-200 flex-shrink-0"
                />
              ) : (
                <div className="w-20 h-20 flex items-center justify-center text-3xl bg-white rounded border border-gray-200 flex-shrink-0">
                  {getAssetIcon(asset.type)}
                </div>
              )}
              <div className="min-w-0">
                <p className="font-medium text-gray-800 break-words">
                  {asset.name}
                </p>
                <p className="text-xs text-gray-500">
                  {formatFileSize(asset.size)}
                </p>
                {asset.tenantName && (
                  <p className="text-xs text-purple-600">
                    Tenant: {asset.tenantName}
                  </p>
                )}
                <p className="text-xs text-gray-400 font-mono truncate">
                  Asset ID: {(asset.uuid || asset.id).toString().substring(0, 12)}
                  ...
                </p>
              </div>
            </div>

            <p className="text-sm font-medium text-gray-700 mt-4">
              Are you sure you want to continue?
            </p>

            <div className="mt-4 flex justify-end gap-2">
              <button
                onClick={() =>
                  setArchiveConfirm({
                    show: false,
                    action: archiveConfirm.action,
                    asset: null,
                  })
                }
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm"
              >
                Cancel
              </button>
              <button
                onClick={() => void confirmArchiveAction()}
                className={`px-4 py-2 rounded-lg text-white text-sm ${isArchive ? "bg-indigo-600 hover:bg-indigo-700" : "bg-emerald-600 hover:bg-emerald-700"}`}
              >
                {isArchive ? "Yes, move to Archive" : "Yes, restore"}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderDebugModal = (): React.ReactElement | null => {
    if (!selectedAssetForDebug) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
          <div className="p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold">🔍 Asset Debug Information</h3>
              <button
                onClick={() => setSelectedAssetForDebug(null)}
                className="text-gray-500 hover:text-gray-700"
              >
                ✕
              </button>
            </div>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-500">File Name</p>
                  <p className="font-medium">{selectedAssetForDebug.name}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">File Size</p>
                  <p className="font-medium">
                    {formatFileSize(selectedAssetForDebug.size)}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">File Type</p>
                  <p className="font-medium">{selectedAssetForDebug.type}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Status</p>
                  <p
                    className={`font-medium ${selectedAssetForDebug.status === "valid" ? "text-green-600" : "text-red-600"}`}
                  >
                    {selectedAssetForDebug.status.toUpperCase()}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Asset ID</p>
                  <p className="font-mono text-xs">
                    {selectedAssetForDebug.assetIdentity?.uuid ||
                      selectedAssetForDebug.id}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Upload Date</p>
                  <p className="font-medium">
                    {selectedAssetForDebug.uploadDate.toLocaleString()}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Retention End Date</p>
                  <p className="font-medium">
                    {selectedAssetForDebug.retentionEndDate.toLocaleString()}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Storage Location</p>
                  <p className="font-medium">
                    {getStorageLabel(selectedAssetForDebug)}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Version</p>
                  <p className="font-medium">
                    v{selectedAssetForDebug.version || 1}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Checksum (SHA-256)</p>
                  <p className="font-mono text-xs truncate">
                    {selectedAssetForDebug.checksum || "N/A"}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Approval Status</p>
                  <p
                    className={`font-medium ${
                      selectedAssetForDebug.approvalStatus === "approved"
                        ? "text-green-600"
                        : selectedAssetForDebug.approvalStatus === "pending"
                          ? "text-yellow-600"
                          : selectedAssetForDebug.approvalStatus === "rejected"
                            ? "text-red-600"
                            : "text-gray-600"
                    }`}
                  >
                    {selectedAssetForDebug.approvalStatus || "N/A"}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Is Latest Version</p>
                  <p className="font-medium">
                    {selectedAssetForDebug.isLatestVersion ? "Yes" : "No"}
                  </p>
                </div>
                {selectedAssetForDebug.s3Key && (
                  <div className="col-span-2">
                    <p className="text-sm text-gray-500">S3 Key</p>
                    <p className="font-mono text-xs break-all">
                      {selectedAssetForDebug.s3Key}
                    </p>
                  </div>
                )}
                {selectedAssetForDebug.etag && (
                  <div className="col-span-2">
                    <p className="text-sm text-gray-500">S3 ETag</p>
                    <p className="font-mono text-xs break-all">
                      {selectedAssetForDebug.etag}
                    </p>
                  </div>
                )}
                {selectedAssetForDebug.collectionId && (
                  <div>
                    <p className="text-sm text-gray-500">Collection ID</p>
                    <p className="font-medium">
                      {selectedAssetForDebug.collectionId}
                    </p>
                  </div>
                )}
                {selectedAssetForDebug.parentId && (
                  <div className="col-span-2">
                    <p className="text-sm text-gray-500">Parent Asset UUID</p>
                    <p className="font-mono text-xs">
                      {selectedAssetForDebug.parentId}
                    </p>
                  </div>
                )}
              </div>

              {(selectedAssetForDebug.linkedEvents?.length || 0) +
                (selectedAssetForDebug.linkedProducts?.length || 0) >
                0 && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <p className="text-sm text-blue-600 font-medium mb-2">
                    🔗 Asset Linkages
                  </p>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs text-gray-500">Linked Events</p>
                      <p className="font-medium">
                        {selectedAssetForDebug.linkedEvents?.length || 0}
                      </p>
                      {selectedAssetForDebug.linkedEvents &&
                        selectedAssetForDebug.linkedEvents.length > 0 && (
                          <div className="mt-1 max-h-20 overflow-y-auto">
                            {selectedAssetForDebug.linkedEvents.map(
                              (eventId, idx) => (
                                <span
                                  key={idx}
                                  className="inline-block text-xs bg-blue-100 text-blue-800 px-2 py-0.5 rounded mr-1 mt-1"
                                >
                                  {eventId}
                                </span>
                              ),
                            )}
                          </div>
                        )}
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Linked Products</p>
                      <p className="font-medium">
                        {selectedAssetForDebug.linkedProducts?.length || 0}
                      </p>
                      {selectedAssetForDebug.linkedProducts &&
                        selectedAssetForDebug.linkedProducts.length > 0 && (
                          <div className="mt-1 max-h-20 overflow-y-auto">
                            {selectedAssetForDebug.linkedProducts.map(
                              (productId, idx) => (
                                <span
                                  key={idx}
                                  className="inline-block text-xs bg-purple-100 text-purple-800 px-2 py-0.5 rounded mr-1 mt-1"
                                >
                                  {productId}
                                </span>
                              ),
                            )}
                          </div>
                        )}
                    </div>
                  </div>
                </div>
              )}

              {selectedAssetForDebug.assetIdentity &&
                selectedAssetForDebug.assetIdentity.versionHistory.length >
                  0 && (
                  <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                    <p className="text-sm text-purple-600 font-medium mb-2">
                      📜 Version History
                    </p>
                    <p className="text-sm text-gray-700">
                      Total Versions:{" "}
                      {selectedAssetForDebug.assetIdentity.versionHistory
                        .length + 1}
                    </p>
                    <p className="text-sm text-gray-700">
                      Current Version: v
                      {selectedAssetForDebug.assetIdentity.currentVersion}
                    </p>
                    <button
                      onClick={() => {
                        setSelectedAssetForVersions(selectedAssetForDebug);
                        setShowVersionHistory(true);
                        setSelectedAssetForDebug(null);
                      }}
                      className="mt-2 px-3 py-1 bg-purple-600 text-white rounded text-xs hover:bg-purple-700"
                    >
                      View Full Version History
                    </button>
                  </div>
                )}

              {selectedAssetForDebug.status === "invalid" && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <p className="text-sm text-red-600 font-medium mb-2">
                    ❌ Validation Failed
                  </p>
                  {selectedAssetForDebug.validationErrors &&
                  selectedAssetForDebug.validationErrors.length > 0 ? (
                    <ul className="list-disc list-inside space-y-1">
                      {selectedAssetForDebug.validationErrors.map(
                        (error, index) => (
                          <li key={index} className="text-sm text-red-700">
                            {error}
                          </li>
                        ),
                      )}
                    </ul>
                  ) : (
                    <p className="text-sm text-red-700">
                      {selectedAssetForDebug.errorReason ||
                        "Unknown validation error"}
                    </p>
                  )}
                </div>
              )}

              {selectedAssetForDebug.metadata && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <p className="text-sm text-blue-600 font-medium mb-2">
                    📋 Complete Metadata
                  </p>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div className="col-span-2">
                      <p className="text-xs text-blue-500 font-medium mb-1">
                        Mandatory Fields
                      </p>
                    </div>
                    <div>
                      <p className="text-gray-500">Asset ID (UUID)</p>
                      <p className="font-mono text-xs">
                        {selectedAssetForDebug.metadata.assetId}
                      </p>
                    </div>
                    <div>
                      <p className="text-gray-500">Original File Name</p>
                      <p className="font-medium">
                        {selectedAssetForDebug.metadata.originalFileName}
                      </p>
                    </div>
                    <div>
                      <p className="text-gray-500">Upload Date & Time</p>
                      <p className="font-medium">
                        {new Date(
                          selectedAssetForDebug.metadata.uploadDateTime,
                        ).toLocaleString()}
                      </p>
                    </div>
                    <div>
                      <p className="text-gray-500">Uploaded By</p>
                      <p className="font-medium">
                        {selectedAssetForDebug.metadata.uploadedBy}
                      </p>
                    </div>
                    <div>
                      <p className="text-gray-500">File Type</p>
                      <p className="font-medium">
                        {selectedAssetForDebug.metadata.fileType}
                      </p>
                    </div>
                    <div>
                      <p className="text-gray-500">Status</p>
                      <p
                        className={`font-medium ${selectedAssetForDebug.metadata.status === "valid" ? "text-green-600" : "text-red-600"}`}
                      >
                        {selectedAssetForDebug.metadata.status}
                      </p>
                    </div>
                    <div>
                      <p className="text-gray-500">File Size</p>
                      <p className="font-medium">
                        {formatFileSize(
                          selectedAssetForDebug.metadata.fileSize,
                        )}
                      </p>
                    </div>
                    <div>
                      <p className="text-gray-500">SHA-256 Checksum</p>
                      <p className="font-mono text-xs truncate">
                        {selectedAssetForDebug.metadata.checksum}
                      </p>
                    </div>
                    <div className="col-span-2">
                      <p className="text-gray-500">Codec Details</p>
                      <p className="font-medium text-xs">
                        {selectedAssetForDebug.metadata.codecDetails || "N/A"}
                      </p>
                    </div>

                    <div className="col-span-2 mt-2">
                      <p className="text-xs text-gray-500 font-medium mb-1">
                        Optional Fields
                      </p>
                    </div>
                    {selectedAssetForDebug.metadata.thumbnailUrl && (
                      <div className="col-span-2">
                        <p className="text-gray-500">Thumbnail URL</p>
                        <div className="mt-1">
                          <img
                            src={selectedAssetForDebug.metadata.thumbnailUrl}
                            alt="Thumbnail"
                            className="h-24 w-auto object-cover rounded border border-gray-200"
                          />
                        </div>
                      </div>
                    )}
                    {selectedAssetForDebug.metadata.collectionId && (
                      <div className="col-span-2">
                        <p className="text-gray-500">Collection ID</p>
                        <p className="font-medium">
                          {selectedAssetForDebug.metadata.collectionId}
                        </p>
                      </div>
                    )}
                    {selectedAssetForDebug.metadata.assetDescription && (
                      <div className="col-span-2">
                        <p className="text-gray-500">Asset Description</p>
                        <p className="font-medium">
                          {selectedAssetForDebug.metadata.assetDescription}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {selectedAssetForDebug.extractedMetadata && (
                <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                  <p className="text-sm text-purple-600 font-medium mb-2">
                    🔍 Extracted Metadata
                  </p>
                  <div className="space-y-2 text-sm">
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <p className="text-gray-500">Extraction Status</p>
                        <p
                          className={`font-medium ${
                            selectedAssetForDebug.extractedMetadata
                              .extractionStatus === "complete"
                              ? "text-green-600"
                              : selectedAssetForDebug.extractedMetadata
                                    .extractionStatus === "partial"
                                ? "text-yellow-600"
                                : "text-red-600"
                          }`}
                        >
                          {
                            selectedAssetForDebug.extractedMetadata
                              .extractionStatus
                          }
                        </p>
                      </div>
                      <div>
                        <p className="text-gray-500">Extraction Date</p>
                        <p className="font-medium">
                          {new Date(
                            selectedAssetForDebug.extractedMetadata
                              .extractionDate,
                          ).toLocaleString()}
                        </p>
                      </div>
                    </div>
                    {selectedAssetForDebug.extractedMetadata.imageWidth && (
                      <div>
                        <p className="text-gray-500">Image Dimensions</p>
                        <p className="font-medium">
                          {selectedAssetForDebug.extractedMetadata.imageWidth}x
                          {selectedAssetForDebug.extractedMetadata.imageHeight}{" "}
                          px
                        </p>
                      </div>
                    )}
                    {selectedAssetForDebug.extractedMetadata.videoDuration && (
                      <div>
                        <p className="text-gray-500">Video Duration</p>
                        <p className="font-medium">
                          {
                            selectedAssetForDebug.extractedMetadata
                              .videoDuration
                          }
                          s
                        </p>
                      </div>
                    )}
                    {selectedAssetForDebug.extractedMetadata.audioBitrate && (
                      <div>
                        <p className="text-gray-500">Audio Bitrate</p>
                        <p className="font-medium">
                          {selectedAssetForDebug.extractedMetadata.audioBitrate}{" "}
                          kbps
                        </p>
                      </div>
                    )}
                    {selectedAssetForDebug.extractedMetadata.missingFields &&
                      selectedAssetForDebug.extractedMetadata.missingFields
                        .length > 0 && (
                        <div>
                          <p className="text-gray-500 text-yellow-600">
                            ⚠️ Missing Fields
                          </p>
                          <p className="font-medium text-xs">
                            {selectedAssetForDebug.extractedMetadata.missingFields.join(
                              ", ",
                            )}
                          </p>
                        </div>
                      )}
                    {selectedAssetForDebug.extractedMetadata.extractionErrors &&
                      selectedAssetForDebug.extractedMetadata.extractionErrors
                        .length > 0 && (
                        <div>
                          <p className="text-gray-500 text-red-600">
                            ❌ Extraction Errors
                          </p>
                          <p className="font-medium text-xs">
                            {selectedAssetForDebug.extractedMetadata.extractionErrors.join(
                              "; ",
                            )}
                          </p>
                        </div>
                      )}
                    <button
                      onClick={() => {
                        setSelectedAssetForMetadata(selectedAssetForDebug);
                        setShowMetadataModal(true);
                        setSelectedAssetForDebug(null);
                      }}
                      className="mt-2 px-3 py-1 bg-purple-600 text-white rounded text-xs hover:bg-purple-700"
                    >
                      View Full Metadata Details
                    </button>
                  </div>
                </div>
              )}

              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-sm text-gray-600 font-medium mb-2">
                  📥 Raw Asset Download
                </p>
                <p className="text-xs text-gray-500 mb-3">
                  Original asset available for 14 days from upload date
                </p>
                <a
                  href={selectedAssetForDebug.url}
                  download={selectedAssetForDebug.name}
                  className="inline-block px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
                >
                  Download Original Asset
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // ============================================================
  // MAIN RENDER
  // ============================================================
  const visibleInvalidAssets = filterTenantId
    ? invalidAssets.filter((a) => !a.tenantId || a.tenantId === filterTenantId)
    : invalidAssets;

  const totalPages = Math.max(1, Math.ceil(totalAssets / pageSize));
  const pageStart = totalAssets === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const pageEnd = Math.min(currentPage * pageSize, totalAssets);
  const pageNumbers: (number | string)[] = [];
  for (let p = 1; p <= totalPages; p++) {
    if (p === 1 || p === totalPages || Math.abs(p - currentPage) <= 1) {
      pageNumbers.push(p);
    } else if (pageNumbers[pageNumbers.length - 1] !== "…") {
      pageNumbers.push("…");
    }
  }

  const percentageUsed = storageMetrics.percentageUsed || 0;

  return (
    <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
      {/* Header */}
      <div className="p-6 bg-gradient-to-r from-blue-600 to-indigo-600 text-white">
        <div className="flex justify-between items-center">
          <div>
            <h2 className="text-2xl font-bold">Asset Manager</h2>
            <p className="text-blue-100 mt-1">
              Upload and manage campaign assets with UUID identity, version
              control, and Glacier archive
            </p>
            <p className="text-xs text-blue-200 mt-1">
              📋 Metadata extracted using browser APIs | 🗄️ S3 + Glacier storage
            </p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <button
              onClick={() => setShowRetentionInfo(!showRetentionInfo)}
              className="px-3 py-1 bg-white bg-opacity-20 rounded-lg text-sm hover:bg-opacity-30 transition"
            >
              ℹ️ Retention Policy
            </button>
            <button
              onClick={() => setSelectedTab("storage")}
              className="px-3 py-1 bg-white bg-opacity-20 rounded-lg text-sm hover:bg-opacity-30 transition"
            >
              📊 Storage
            </button>
          </div>
        </div>
      </div>

      {/* Retention Info Panel */}
      {showRetentionInfo && (
        <div className="m-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <h3 className="font-semibold text-blue-800 mb-2">
            📋 Asset Retention Policy (14 Days)
          </h3>
          <div className="grid md:grid-cols-2 gap-4 text-sm">
            <div>
              <p className="font-medium text-blue-700">
                📥 Temporary Storage (14 Days)
              </p>
              <ul className="list-disc list-inside text-gray-700 mt-1 space-y-1">
                <li>All uploaded assets stored in temp storage for 14 days</li>
                <li>Available for debugging and reprocessing</li>
                <li>Operations team can access and retrieve assets</li>
                <li>Auto-expiry tracking with daily checks</li>
              </ul>
            </div>
            <div>
              <p className="font-medium text-blue-700">
                🗄️ Long-term Storage (After 14 Days)
              </p>
              <ul className="list-disc list-inside text-gray-700 mt-1 space-y-1">
                <li>Automatic transition to long-term storage</li>
                <li>No manual intervention required</li>
                <li>Complete audit trail maintained</li>
                <li>No data loss during transition</li>
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Tenant filter + Asset Ingestion launcher */}
      <div className="mx-4 mt-4 p-4 bg-white border border-gray-200 rounded-lg flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <label className="text-sm font-medium text-gray-700">🏢 Tenant</label>
          <select
            value={filterTenantId}
            onChange={(e) => {
              setFilterTenantId(e.target.value);
              setCurrentPage(1);
            }}
            disabled={tenantsLoading}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 min-w-[220px]"
          >
            <option value="">
              {tenantsLoading ? "Loading tenants..." : "All tenants"}
            </option>
            {tenants.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
          </select>
        </div>
        <button
          onClick={() => {
            setUploadTenantId("");
            setShowIngestionModal(true);
          }}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700"
        >
          📤 Asset Ingestion
        </button>
      </div>

      {!showIngestionModal && archiveMessage && (
        <div
          className={`mx-4 mt-3 px-3 py-2 rounded-lg text-sm ${
            archiveMessage.type === "success"
              ? "bg-green-50 text-green-700 border border-green-200"
              : "bg-red-50 text-red-700 border border-red-200"
          }`}
        >
          {archiveMessage.text}
        </div>
      )}

      {/* Asset Ingestion Modal */}
      {showIngestionModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-40 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl w-full max-w-5xl max-h-[92vh] overflow-y-auto shadow-2xl">
            <div className="sticky top-0 z-10 flex items-center justify-between px-6 py-4 bg-white border-b border-gray-200">
              <div>
                <h3 className="text-xl font-bold text-gray-800">
                  📤 Asset Ingestion
                </h3>
                <p className="text-xs text-gray-500 mt-1">
                  Select a tenant first. Uploads are stored for that tenant
                  only.
                </p>
              </div>
              <button
                onClick={() => setShowIngestionModal(false)}
                disabled={uploading}
                className="text-gray-500 hover:text-gray-700 disabled:opacity-40"
                title={uploading ? "Upload in progress" : "Close"}
              >
                ✕
              </button>
            </div>

            {/* Glacier Retrieval Section (from development branch) */}
            <div className="mx-4 mt-4 p-4 bg-slate-50 border border-slate-200 rounded-lg">
              <div className="flex flex-col lg:flex-row lg:items-end gap-3">
                <div className="flex-1">
                  <h3 className="font-semibold text-slate-800">
                    🗄️ Glacier Retrieval
                  </h3>
                  <p className="text-sm text-slate-600 mt-1">
                    Restore an archived asset by entering the original uploaded
                    file name.
                  </p>
                </div>
                <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
                  <input
                    type="text"
                    value={retrieveFileName}
                    onChange={(event) =>
                      setRetrieveFileName(event.target.value)
                    }
                    placeholder="example-file.pdf"
                    className="w-full sm:w-72 px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                  <button
                    onClick={handleRestoreByFileName}
                    disabled={archiveActionId === "retrieve-by-name"}
                    className="px-4 py-2 bg-slate-800 text-white rounded-lg text-sm hover:bg-slate-900 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {archiveActionId === "retrieve-by-name"
                      ? "Requesting..."
                      : "Retrieve"}
                  </button>
                </div>
              </div>
              {archiveMessage && (
                <div
                  className={`mt-3 px-3 py-2 rounded-lg text-sm ${
                    archiveMessage.type === "success"
                      ? "bg-green-50 text-green-700 border border-green-200"
                      : "bg-red-50 text-red-700 border border-red-200"
                  }`}
                >
                  {archiveMessage.text}
                </div>
              )}
            </div>

            {/* Upload Form */}
            <div className="mx-4 mt-4">
              <div className="grid grid-cols-1 md:grid-cols-2 items-start gap-x-4 gap-y-4 bg-gray-50 p-4 rounded-lg border border-gray-200">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Tenant{" "}
                    <span className="text-red-500 text-xs">(required)</span>
                  </label>
                  <select
                    value={uploadTenantId}
                    onChange={(e) => setUploadTenantId(e.target.value)}
                    disabled={uploading || tenantsLoading}
                    className={`w-full h-10 px-3 border rounded-lg bg-white text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent ${uploadTenantId ? "border-gray-300" : "border-red-300"}`}
                  >
                    <option value="">
                      {tenantsLoading
                        ? "Loading tenants..."
                        : "Select a tenant"}
                    </option>
                    {tenants.map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.name}
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-400 mt-1 truncate">
                    {uploadTenantId
                      ? `Uploading to: ${tenants.find((t) => t.id === uploadTenantId)?.name || ""}`
                      : "Select a tenant to enable upload"}
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Collection ID{" "}
                    <span className="text-gray-400 text-xs">(optional)</span>
                  </label>
                  <input
                    type="text"
                    value={uploadFormData.collectionId}
                    onChange={(e) =>
                      setUploadFormData({
                        ...uploadFormData,
                        collectionId: e.target.value,
                      })
                    }
                    placeholder="Enter Collection ID"
                    className="w-full h-10 px-3 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    disabled={uploading}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Asset Description{" "}
                    <span className="text-gray-400 text-xs">(optional)</span>
                  </label>
                  <input
                    type="text"
                    value={uploadFormData.assetDescription}
                    onChange={(e) =>
                      setUploadFormData({
                        ...uploadFormData,
                        assetDescription: e.target.value,
                      })
                    }
                    placeholder="Add a description for your asset"
                    className="w-full h-10 px-3 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    disabled={uploading}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Linked Events/Products{" "}
                    <span className="text-gray-400 text-xs">
                      (comma separated)
                    </span>
                  </label>
                  <input
                    type="text"
                    value={uploadFormData.linkedEvents.join(", ")}
                    onChange={(e) => {
                      const values = e.target.value
                        .split(",")
                        .map((s) => s.trim())
                        .filter(Boolean);
                      setUploadFormData({
                        ...uploadFormData,
                        linkedEvents: values,
                      });
                    }}
                    placeholder="EVT-001, EVT-002"
                    className="w-full h-10 px-3 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    disabled={uploading}
                  />
                </div>
              </div>
            </div>

            {/* Upload Area */}
            <div className="p-6">
              <div
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                className={`
                        border-2 border-dashed rounded-xl p-8 text-center transition-all duration-200
                        ${
                          isDragging
                            ? "border-blue-500 bg-blue-50"
                            : "border-gray-300 hover:border-blue-400 hover:bg-gray-50"
                        }
                       ${uploading ? "opacity-50 pointer-events-none" : !uploadTenantId ? "opacity-60 cursor-not-allowed" : "cursor-pointer"}
                    `}
                onClick={() => {
                  if (!uploadTenantId) {
                    alert("Please select a tenant first.");
                    return;
                  }
                  fileInputRef.current?.click();
                }}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept={getAcceptString()}
                  className="hidden"
                  onChange={handleFileSelect}
                />

                <div className="flex flex-col items-center gap-3">
                  <div className="w-20 h-20 rounded-full bg-blue-100 flex items-center justify-center">
                    <svg
                      className="w-10 h-10 text-blue-600"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                      />
                    </svg>
                  </div>
                  <div>
                    <p className="text-gray-700 font-medium text-lg">
                      {!uploadTenantId
                        ? "Select a tenant to enable uploads"
                        : isDragging
                          ? "Drop files here"
                          : "Drag & drop files here"}
                    </p>
                    <p className="text-sm text-gray-500 mt-1">
                      or click to browse
                    </p>
                  </div>
                  <div className="text-xs text-gray-400 max-w-md">
                    <p>
                      🎬 Supported Media: All Images | All Videos | All Audio
                    </p>
                    <p className="mt-1">
                      📏 Limits: Videos up to <strong>40GB</strong> and{" "}
                      <strong>1 hour</strong> | Images & Audio up to{" "}
                      <strong>2GB</strong>
                    </p>
                    <p className="mt-1">
                      🔑 SHA-256 checksum | UUID identity | Version control
                    </p>
                    <p className="mt-1 text-blue-600">
                      📋 Metadata extracted using browser APIs
                    </p>
                    <p className="mt-1 text-indigo-600">
                      🗄️ S3 + Glacier storage with 14-day retention
                    </p>
                  </div>
                </div>
              </div>

              {/* Upload Progress */}
              {uploading && (
                <div className="mt-6">
                  <div className="flex justify-between text-sm text-gray-600 mb-2">
                    <span>Processing files...</span>
                    <span>{Math.round(uploadProgress)}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
                    <div
                      className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${uploadProgress}%` }}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Asset Tabs */}
      {(assets.length > 0 ||
        invalidAssets.length > 0 ||
        filterTenantId ||
        assetsLoading) && (
        <div className="border-t border-gray-200">
          <div className="flex border-b border-gray-200">
            <button
              onClick={() => setSelectedTab("valid")}
              className={`flex-1 py-3 text-center font-medium transition ${
                selectedTab === "valid"
                  ? "text-blue-600 border-b-2 border-blue-600 bg-blue-50"
                  : "text-gray-500 hover:text-gray-700"
              }`}
            >
              ✅Valid Assets ({totalAssets})
            </button>
            <button
              onClick={() => setSelectedTab("invalid")}
              className={`flex-1 py-3 text-center font-medium transition ${
                selectedTab === "invalid"
                  ? "text-red-600 border-b-2 border-red-600 bg-red-50"
                  : "text-gray-500 hover:text-gray-700"
              }`}
            >
              ❌ Invalid/Unsupported ({visibleInvalidAssets.length})
            </button>
            <button
              onClick={() => setSelectedTab("storage")}
              className={`flex-1 py-3 text-center font-medium transition ${
                selectedTab === "storage"
                  ? "text-green-600 border-b-2 border-green-600 bg-green-50"
                  : "text-gray-500 hover:text-gray-700"
              }`}
            >
              📊 Storage Monitor
            </button>
          </div>

          {selectedTab === "valid" && !assetsLoading && assets.length === 0 && (
            <div className="p-10 text-center text-gray-500">
              <p>
                No assets found
                {filterTenantId
                  ? ` for ${tenants.find((t) => t.id === filterTenantId)?.name || "this tenant"}`
                  : ""}
                .
              </p>
            </div>
          )}

          {selectedTab === "valid" && assetsLoading && (
            <div className="flex flex-col items-center justify-center py-20 text-gray-500">
              <div className="w-10 h-10 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
              <p className="mt-3 text-sm">
                Loading assets
                {filterTenantId
                  ? ` for ${tenants.find((t) => t.id === filterTenantId)?.name || "tenant"}`
                  : ""}
                ...
              </p>
            </div>
          )}

          {/* Valid Assets Tab */}
          {selectedTab === "valid" && !assetsLoading && assets.length > 0 && (
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <div>
                  <h3 className="font-semibold text-gray-800">
                    Uploaded Assets
                  </h3>
                  <p className="text-xs text-gray-500 mt-1">
                    🔑 UUID: Each asset has a unique identifier
                  </p>
                  {duplicateCandidates.length > 0 && (
                    <button
                      onClick={() => setShowDuplicateDetection(true)}
                      className="text-sm text-red-600 hover:text-red-800 mt-1"
                    >
                      ⚠️ {duplicateCandidates.length} duplicate(s) detected
                    </button>
                  )}
                </div>
                <div className="text-sm text-gray-500">
                  {catalogSummary
                    ? catalogSummary.tempCount
                    : assets.filter((a) => a.storageLocation === "temp")
                        .length}{" "}
                  in temp storage.
                  {catalogSummary
                    ? catalogSummary.longtermCount + catalogSummary.glacierCount
                    : assets.filter(
                        (a) =>
                          a.storageLocation === "longterm" ||
                          a.storageLocation === "glacier",
                      ).length}{" "}
                  in long-term/Glacier
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {assets.map((asset) => (
                  <div
                    key={asset.id}
                    className={`border rounded-lg p-4 hover:shadow-md transition ${
                      asset.isDuplicate
                        ? "border-red-300 bg-red-50"
                        : asset.storageLocation === "glacier"
                          ? "border-indigo-300 bg-indigo-50"
                          : asset.storageLocation === "restoring"
                            ? "border-amber-300 bg-amber-50"
                            : asset.storageLocation === "archiving"
                              ? "border-blue-300 bg-blue-50"
                              : ""
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-3 flex-1 min-w-0">
                        <div className="text-3xl flex-shrink-0">
                          {getAssetIcon(asset.type)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p
                            className="font-medium text-gray-800 break-words truncate"
                            title={asset.name}
                          >
                            {asset.name}
                            {asset.version > 1 && (
                              <span className="text-xs text-gray-400 ml-1 flex-shrink-0">
                                v{asset.version}
                              </span>
                            )}
                          </p>
                          {asset.formattedName && (
                            <p
                              className="text-xs font-medium text-purple-700 break-words"
                              title={asset.formattedName}
                            >
                              Formatted: {asset.formattedName}
                            </p>
                          )}
                          <p className="text-xs text-gray-500">
                            {formatFileSize(asset.size)}
                          </p>
                          <p className="text-xs text-gray-400">
                            Uploaded: {asset.uploadDate.toLocaleDateString()}
                          </p>
                          <p className="text-xs text-blue-600 break-words">
                            {asset.storageLocation === "temp"
                              ? asset.hasRetentionPolicy === false
                                ? "⚠️ Retention date unavailable (legacy parent asset)"
                                : `⏳ ${getDaysRemaining(asset.retentionEndDate)} days left — available until ${asset.retentionEndDate.toLocaleString()}`
                              : asset.storageLocation === "glacier"
                                ? asset.restoreAvailableUntil
                                  ? `✅ Restored copy available for ${asset.restoreDaysRemaining ?? 0} days (until ${new Date(asset.restoreAvailableUntil).toLocaleString()})`
                                  : "❄️ Archived in Glacier"
                                : asset.storageLocation === "restoring"
                                  ? `🔄 Restore in progress${asset.restoreEstimate ? ` — ${asset.restoreEstimate}` : ""}`
                                  : asset.storageLocation === "archiving"
                                    ? "❄️ Moving to Glacier automatically"
                                    : "📦 In long-term storage"}
                          </p>
                          <p
                            className={`text-xs ${asset.isArchived ? "text-indigo-700" : "text-gray-500"}`}
                          >
                            DB archive status:{" "}
                            {asset.isArchived ? "Archived" : "Not archived"}
                          </p>
                          {asset.restoredAccessUrl && (
                            <a
                              href={asset.restoredAccessUrl}
                              target="_blank"
                              rel="noreferrer"
                              className="text-xs text-emerald-700 underline"
                            >
                              Open restored file
                            </a>
                          )}
                          {asset.collectionId && (
                            <p
                              className="text-xs text-purple-600 truncate"
                              title={asset.collectionId}
                            >
                              Collection: {asset.collectionId}
                            </p>
                          )}
                          {asset.isDuplicate && (
                            <p className="text-xs text-red-600">
                              ⚠️ Duplicate asset
                            </p>
                          )}
                          {asset.s3Key && (
                            <p
                              className="text-[10px] text-gray-400 truncate max-w-[180px]"
                              title={asset.s3Key}
                            >
                              S3: {asset.s3Key}
                            </p>
                          )}
                          <p
                            className="text-xs text-gray-400 font-mono truncate mt-1"
                            title={asset.assetIdentity?.uuid || asset.id}
                          >
                            Asset ID:{" "}
                            {(asset.assetIdentity?.uuid || asset.id).substring(
                              0,
                              12,
                            )}
                            ...
                          </p>
                          {asset.approvalStatus && (
                            <p
                              className={`text-xs mt-1 ${
                                asset.approvalStatus === "approved"
                                  ? "text-green-600"
                                  : asset.approvalStatus === "pending"
                                    ? "text-yellow-600"
                                    : asset.approvalStatus === "rejected"
                                      ? "text-red-600"
                                      : "text-orange-600"
                              }`}
                            >
                              {asset.approvalStatus === "approved" &&
                                "✅ Approved"}
                              {asset.approvalStatus === "pending" &&
                                "⏳ Pending Approval"}
                              {asset.approvalStatus === "rejected" &&
                                "❌ Rejected"}
                              {asset.approvalStatus ===
                                "modification_requested" &&
                                "🔄 Modification Requested"}
                            </p>
                          )}
                          {(asset.linkedEvents?.length || 0) +
                            (asset.linkedProducts?.length || 0) >
                            0 && (
                            <p
                              className="text-xs text-blue-600 mt-1 truncate"
                              title={`${asset.linkedEvents?.length || 0} events, ${asset.linkedProducts?.length || 0} products`}
                            >
                              🔗 {asset.linkedEvents?.length || 0} events,{" "}
                              {asset.linkedProducts?.length || 0} products
                            </p>
                          )}
                          {asset.extractedMetadata && (
                            <p
                              className={`text-xs mt-1 ${
                                asset.extractedMetadata.extractionStatus ===
                                "complete"
                                  ? "text-green-600"
                                  : asset.extractedMetadata.extractionStatus ===
                                      "partial"
                                    ? "text-yellow-600"
                                    : "text-red-600"
                              }`}
                            >
                              {asset.extractedMetadata.extractionStatus ===
                                "complete" && "✅ Metadata extracted"}
                              {asset.extractedMetadata.extractionStatus ===
                                "partial" && "⚠️ Partial metadata extraction"}
                              {asset.extractedMetadata.extractionStatus ===
                                "failed" && "❌ Metadata extraction failed"}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="flex flex-col gap-1 flex-shrink-0 ml-2">
                        <button
                          onClick={() => handleViewVersionHistory(asset)}
                          className="text-gray-400 hover:text-purple-600 text-sm"
                          title="View Version History"
                        >
                          📜
                        </button>
                        {asset.extractedMetadata && (
                          <button
                            onClick={() => handleViewMetadata(asset)}
                            className="text-blue-400 hover:text-blue-600 text-sm"
                            title="View Extracted Metadata"
                          >
                            📋
                          </button>
                        )}
                        <button
                          onClick={() => handleDebugAsset(asset)}
                          className="text-gray-400 hover:text-blue-600 text-sm"
                          title="Debug Asset"
                        >
                          🔍
                        </button>
                        <button
                          onClick={() => handleDeleteAsset(asset.id, false)}
                          className="text-gray-400 hover:text-red-600 text-sm"
                          title="Delete Asset"
                        >
                          🗑️
                        </button>
                      </div>
                    </div>
                    {asset.thumbnailUrl && asset.type.startsWith("image/") && (
                      <img
                        src={asset.thumbnailUrl}
                        alt={asset.name}
                        className="mt-3 w-full h-32 object-cover rounded"
                      />
                    )}
                    {asset.type.startsWith("video/") && (
                      <video
                        src={asset.url}
                        className="mt-3 w-full h-32 object-cover rounded"
                        controls
                      />
                    )}
                    {asset.type.startsWith("audio/") && (
                      <div className="mt-3 p-4 bg-gray-100 rounded flex items-center justify-center">
                        <div className="text-center">
                          <div className="text-4xl mb-2">🎵</div>
                          <p className="text-sm text-gray-600">Audio File</p>
                          <p className="text-xs text-gray-400">
                            {formatFileSize(asset.size)}
                          </p>
                        </div>
                      </div>
                    )}
                    {/* Glacier Actions */}
                    <div className="mt-3 flex flex-wrap gap-2">
                      <button
                        onClick={() =>
                          setArchiveConfirm({
                            show: true,
                            action: "archive",
                            asset,
                          })
                        }
                        disabled={
                          !asset.s3Key ||
                          asset.storageLocation === "glacier" ||
                          asset.storageLocation === "restoring" ||
                          asset.storageLocation === "archiving" ||
                          archiveActionId === asset.id
                        }
                        className="px-3 py-1.5 bg-indigo-600 text-white rounded text-xs hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {archiveActionId === asset.id
                          ? "Working..."
                          : "❄️ Move to Archive"}
                      </button>
                      <button
                        onClick={() =>
                          asset.storageLocation === "restoring" ||
                          asset.restoreAvailableUntil
                            ? void checkRestoreStatus(asset)
                            : setArchiveConfirm({
                                show: true,
                                action: "restore",
                                asset,
                              })
                        }
                        disabled={
                          !asset.s3Key ||
                          (asset.storageLocation !== "glacier" &&
                            asset.storageLocation !== "restoring") ||
                          archiveActionId === asset.id
                        }
                        className="px-3 py-1.5 bg-emerald-600 text-white rounded text-xs hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {asset.storageLocation === "restoring"
                          ? "🔄 Check Restore Status"
                          : asset.restoreAvailableUntil
                            ? "🔄 Refresh Access Link"
                            : "🔄 Restore"}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {selectedTab === "valid" && !assetsLoading && totalAssets > 0 && (
            <div className="mx-6 mb-6 pt-4 border-t border-gray-100 flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <span>Show</span>
                <select
                  value={pageSize}
                  onChange={(e) => {
                    setPageSize(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                  className="h-9 px-2 border border-gray-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-blue-500"
                >
                  {[8, 16, 24, 48, 96].map((n) => (
                    <option key={n} value={n}>
                      {n}
                    </option>
                  ))}
                </select>
                <span>per page</span>
              </div>

              <p className="text-sm text-gray-500">
                Showing {pageStart}–{pageEnd} of {totalAssets}
              </p>

              <div className="flex items-center gap-1">
                <button
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage <= 1}
                  className="h-9 px-3 border border-gray-300 rounded-lg text-sm hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  ‹ Prev
                </button>
                {pageNumbers.map((p, idx) =>
                  typeof p === "number" ? (
                    <button
                      key={`p-${p}`}
                      onClick={() => setCurrentPage(p)}
                      className={`h-9 min-w-[2.25rem] px-2 rounded-lg text-sm border ${
                        p === currentPage
                          ? "bg-blue-600 text-white border-blue-600"
                          : "border-gray-300 hover:bg-gray-50 text-gray-700"
                      }`}
                    >
                      {p}
                    </button>
                  ) : (
                    <span key={`e-${idx}`} className="px-1 text-gray-400">
                      …
                    </span>
                  ),
                )}
                <button
                  onClick={() =>
                    setCurrentPage(Math.min(totalPages, currentPage + 1))
                  }
                  disabled={currentPage >= totalPages}
                  className="h-9 px-3 border border-gray-300 rounded-lg text-sm hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  Next ›
                </button>
              </div>
            </div>
          )}

          {/* Invalid Assets Tab */}
          {selectedTab === "invalid" && (
            <div className="p-6">
              {visibleInvalidAssets.length > 0 ? (
                <>
                  <div className="mb-4 flex justify-between items-center">
                    <div>
                      <h3 className="font-semibold text-red-800">
                        ❌ Invalid/Unsupported Assets
                      </h3>
                      <p className="text-sm text-gray-600 mt-1">
                        {visibleInvalidAssets.length} asset(s) failed upload or
                        validation. Review the error and retry the upload.
                      </p>
                    </div>
                    <button
                      onClick={handleReuploadAllInvalid}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
                    >
                      Retry All Failed Uploads
                    </button>
                  </div>
                  <div className="space-y-3">
                    {visibleInvalidAssets.map((asset) => (
                      <div
                        key={asset.id}
                        className="border border-red-200 rounded-lg p-4 bg-red-50"
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex items-center gap-3 flex-1">
                            <div className="text-3xl">
                              {getAssetIcon(asset.type)}
                            </div>
                            <div className="flex-1">
                              <p className="font-medium text-gray-800">
                                {asset.name}
                              </p>
                              <div className="mt-1">
                                {asset.validationErrors &&
                                asset.validationErrors.length > 0 ? (
                                  <div className="space-y-1">
                                    {asset.validationErrors.map(
                                      (error, idx) => (
                                        <p
                                          key={idx}
                                          className="text-sm text-red-600"
                                        >
                                          ❌ {error}
                                        </p>
                                      ),
                                    )}
                                  </div>
                                ) : (
                                  <p className="text-sm text-red-600">
                                    ❌{" "}
                                    {asset.errorReason || "Validation failed"}
                                  </p>
                                )}
                              </div>
                              {asset.collectionId && (
                                <p className="text-xs text-purple-600 mt-1">
                                  Collection: {asset.collectionId}
                                </p>
                              )}
                              {asset.assetDescription && (
                                <p className="text-xs text-gray-500 mt-1">
                                  Description: {asset.assetDescription}
                                </p>
                              )}
                              <div className="mt-2 flex gap-2 flex-wrap">
                                <button
                                  onClick={() =>
                                    handleReuploadInvalid(asset.id)
                                  }
                                  className="px-3 py-1 bg-green-600 text-white rounded text-xs hover:bg-green-700"
                                >
                                  Retry Upload
                                </button>
                                <button
                                  onClick={() => handleDebugAsset(asset)}
                                  className="px-3 py-1 bg-gray-600 text-white rounded text-xs hover:bg-gray-700"
                                >
                                  Debug Info
                                </button>
                                <button
                                  onClick={() =>
                                    handleDeleteAsset(asset.id, true)
                                  }
                                  className="px-3 py-1 bg-red-600 text-white rounded text-xs hover:bg-red-700"
                                >
                                  Remove
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <p>✅ No invalid assets found.</p>
                  <p className="text-sm mt-1">
                    All uploaded assets passed validation.
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Storage Monitoring Tab */}
          {selectedTab === "storage" && (
            <div className="p-6">
              <h3 className="font-semibold text-gray-800 mb-4">
                📊 Storage Monitoring Dashboard
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                  <p className="text-sm text-gray-600">Total Capacity</p>
                  <p className="text-2xl font-bold text-blue-700">
                    {formatFileSize(storageMetrics.totalCapacity)}
                  </p>
                </div>
                <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                  <p className="text-sm text-gray-600">Used Storage</p>
                  <p className="text-2xl font-bold text-green-700">
                    {formatFileSize(storageMetrics.usedCapacity)}
                  </p>
                </div>
                <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
                  <p className="text-sm text-gray-600">Available Storage</p>
                  <p className="text-2xl font-bold text-yellow-700">
                    {formatFileSize(storageMetrics.availableCapacity)}
                  </p>
                </div>
                <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                  <p className="text-sm text-gray-600">Usage Percentage</p>
                  <p className="text-2xl font-bold text-purple-700">
                    {percentageUsed.toFixed(1)}%
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                  <p className="text-sm text-gray-600">📥 Temporary Storage</p>
                  <p className="text-xl font-semibold text-blue-600">
                    {formatFileSize(storageMetrics.tempStorageUsed)}
                  </p>
                  <p className="text-xs text-gray-400">
                    Assets with 14-day retention
                  </p>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                  <p className="text-sm text-gray-600">🗄️ Long-term Storage</p>
                  <p className="text-xl font-semibold text-gray-700">
                    {formatFileSize(storageMetrics.longTermStorageUsed)}
                  </p>
                  <p className="text-xs text-gray-400">Archived assets</p>
                </div>
                <div className="bg-indigo-50 p-4 rounded-lg border border-indigo-200">
                  <p className="text-sm text-gray-600">❄️ Glacier Storage</p>
                  <p className="text-xl font-semibold text-indigo-700">
                    {formatFileSize(
                      catalogSummary
                        ? catalogSummary.glacierSize
                        : assets
                            .filter((a) => a.storageLocation === "glacier")
                            .reduce((sum, a) => sum + a.size, 0),
                    )}
                  </p>
                  <p className="text-xs text-gray-400">
                    {catalogSummary
                      ? catalogSummary.glacierCount
                      : assets.filter((a) => a.storageLocation === "glacier")
                          .length}{" "}
                    assets archived
                  </p>
                </div>
              </div>

              <div className="mb-4">
                <div className="flex justify-between text-sm text-gray-600 mb-2">
                  <span>Storage Usage</span>
                  <span>{percentageUsed.toFixed(1)}% used</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-4 overflow-hidden">
                  <div
                    className={`h-4 rounded-full transition-all duration-500 ${getStorageStatusColor(percentageUsed)}`}
                    style={{ width: `${Math.min(percentageUsed, 100)}%` }}
                  />
                </div>
                {percentageUsed > 90 && (
                  <div className="mt-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-sm text-red-800">
                      🔴 Storage limit exceeded! Immediate action required.
                    </p>
                  </div>
                )}
              </div>

              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                <h4 className="font-medium text-gray-700 mb-2">
                  📈 Storage Insights
                </h4>
                <ul className="space-y-1 text-sm text-gray-600">
                  <li>
                    • Total assets:{" "}
                    {(catalogSummary
                      ? catalogSummary.totalCount
                      : assets.length) + invalidAssets.length}
                  </li>
                  <li>
                    • Active in temp:{" "}
                    {catalogSummary
                      ? catalogSummary.tempCount
                      : assets.filter((a) => a.storageLocation === "temp")
                          .length}
                  </li>
                  <li>
                    • Archived:{" "}
                    {catalogSummary
                      ? catalogSummary.longtermCount
                      : assets.filter((a) => a.storageLocation === "longterm")
                          .length}
                  </li>
                  <li>
                    • In Glacier:{" "}
                    {catalogSummary
                      ? catalogSummary.glacierCount
                      : assets.filter((a) => a.storageLocation === "glacier")
                          .length}
                  </li>
                  <li>
                    • Restoring:{" "}
                    {catalogSummary
                      ? catalogSummary.restoringCount
                      : assets.filter((a) => a.storageLocation === "restoring")
                          .length}
                  </li>
                  <li>• Invalid assets: {invalidAssets.length}</li>
                  <li>
                    • Assets with extracted metadata:{" "}
                    {assets.filter((a) => a.extractedMetadata).length}
                  </li>
                  <li>
                    • Assets with approvals:{" "}
                    {assets.filter((a) => a.approvalStatus).length}
                  </li>
                  <li>
                    • Assets with linkages:{" "}
                    {
                      assets.filter(
                        (a) =>
                          (a.linkedEvents?.length || 0) +
                            (a.linkedProducts?.length || 0) >
                          0,
                      ).length
                    }
                  </li>
                  {duplicateCandidates.length > 0 && (
                    <li className="text-red-600">
                      • ⚠️ {duplicateCandidates.length} duplicate assets
                      detected
                    </li>
                  )}
                  <li className="text-blue-600">
                    • 📝 Upload logs stored: {uploadAttemptLogs.length} entries
                  </li>
                  <li className="text-green-600">
                    • 🔑 SHA-256 checksums:{" "}
                    {assets.filter((a) => a.checksum).length} assets
                  </li>
                  <li className="text-indigo-600">
                    • ❄️ Assets with S3 keys:{" "}
                    {assets.filter((a) => a.s3Key).length}
                  </li>
                </ul>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Validation Modal */}
      {showValidationModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-hidden shadow-2xl">
            <div className="p-6 border-b border-gray-200">
              <div className="flex justify-between items-center">
                <div>
                  <h3 className="text-xl font-bold text-gray-800">
                    {modalOverallStatus === "validating"
                      ? "🔄 Validating Asset"
                      : modalOverallStatus === "success"
                        ? "✅ Validation Complete"
                        : "❌ Validation Failed"}
                  </h3>
                  <p className="text-sm text-gray-500 mt-1">
                    {modalAssetName} • File {currentFileIndex} of {totalFiles}
                  </p>
                  <div className="mt-2">
                    <div className="w-full bg-gray-200 rounded-full h-1.5">
                      <div
                        className="bg-blue-600 h-1.5 rounded-full transition-all duration-300"
                        style={{
                          width: `${Math.min((validationChecks.filter((c) => c.status !== "pending").length / validationChecks.length) * 100, 100)}%`,
                        }}
                      />
                    </div>
                    <div className="flex justify-between text-xs text-gray-400 mt-1">
                      <span>
                        {
                          validationChecks.filter((c) => c.status === "success")
                            .length
                        }{" "}
                        passed
                      </span>
                      <span>
                        {
                          validationChecks.filter((c) => c.status === "error")
                            .length
                        }{" "}
                        failed
                      </span>
                      <span>
                        {
                          validationChecks.filter((c) => c.status === "warning")
                            .length
                        }{" "}
                        warnings
                      </span>
                    </div>
                  </div>
                </div>
                <div className="text-sm text-gray-400 text-right">
                  {modalOverallStatus === "validating" && (
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                      <span>Processing...</span>
                    </div>
                  )}
                  {modalOverallStatus === "success" && (
                    <span className="text-green-500 font-medium">✓ Passed</span>
                  )}
                  {modalOverallStatus === "error" && (
                    <span className="text-red-500 font-medium">✗ Failed</span>
                  )}
                  {modalOverallStatus !== "validating" && (
                    <button
                      onClick={() => {
                        setShowValidationModal(false);
                        setManualCloseModal(true);
                      }}
                      className="mt-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium"
                    >
                      Close
                    </button>
                  )}
                </div>
              </div>
            </div>

            <div className="p-6 overflow-y-auto max-h-[500px]">
              <div className="space-y-2">
                {validationChecks.map((check) => (
                  <div
                    key={check.id}
                    className={`flex items-center gap-3 p-3 rounded-lg border transition-all duration-300 ${getCheckStatusColor(check.status)}`}
                  >
                    <div className="flex-shrink-0 w-6 h-6 flex items-center justify-center">
                      {getCheckStatusIcon(check.status)}
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-700">
                        {check.label}
                      </p>
                      {check.message && (
                        <p
                          className={`text-xs mt-0.5 ${
                            check.status === "error"
                              ? "text-red-600"
                              : check.status === "success"
                                ? "text-green-600"
                                : check.status === "warning"
                                  ? "text-yellow-600"
                                  : "text-gray-500"
                          }`}
                        >
                          {check.message}
                        </p>
                      )}
                    </div>
                    {check.timestamp && (
                      <div className="text-xs text-gray-400 flex-shrink-0">
                        {check.timestamp.toLocaleTimeString()}
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {/* Summary Section */}
              {modalOverallStatus !== "validating" && (
                <div className="mt-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <h4 className="font-semibold text-gray-700 mb-2">
                    📊 Validation Summary
                  </h4>
                  <div className="grid grid-cols-3 gap-4 text-center">
                    <div>
                      <p className="text-2xl font-bold text-green-600">
                        {
                          validationChecks.filter((c) => c.status === "success")
                            .length
                        }
                      </p>
                      <p className="text-xs text-gray-500">Passed</p>
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-red-600">
                        {
                          validationChecks.filter((c) => c.status === "error")
                            .length
                        }
                      </p>
                      <p className="text-xs text-gray-500">Failed</p>
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-yellow-600">
                        {
                          validationChecks.filter((c) => c.status === "warning")
                            .length
                        }
                      </p>
                      <p className="text-xs text-gray-500">Warnings</p>
                    </div>
                  </div>
                  {modalOverallStatus === "success" && (
                    <p className="text-center text-sm text-green-600 mt-2">
                      ✅ All checks passed! Asset is valid.
                    </p>
                  )}
                  {modalOverallStatus === "error" && (
                    <p className="text-center text-sm text-red-600 mt-2">
                      ❌ Some checks failed. Retry the upload below. After the
                      third failed attempt, you will be taken to the ticketing
                      system.
                    </p>
                  )}
                </div>
              )}
            </div>

            <div className="p-4 border-t border-gray-200 bg-gray-50">
              <div className="flex justify-between items-center">
                <div className="text-sm text-gray-600">
                  {modalOverallStatus === "validating" && (
                    <span>⏳ Running checks...</span>
                  )}
                  {modalOverallStatus === "success" && (
                    <span className="text-green-600">✅ All checks passed</span>
                  )}
                  {modalOverallStatus === "error" && (
                    <span className="text-red-600">❌ Some checks failed</span>
                  )}
                </div>
                {modalOverallStatus !== "validating" && (
                  <div className="flex items-center gap-2">
                    {modalOverallStatus === "error" && (
                      <button
                        type="button"
                        onClick={() => {
                          const failedFiles = lastValidationFiles.filter(
                            (file) => file.name === modalAssetName,
                          );
                          setShowValidationModal(false);
                          setManualCloseModal(true);
                          if (failedFiles.length > 0) {
                            void processFiles(failedFiles);
                          } else {
                            fileInputRef.current?.click();
                          }
                        }}
                        className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm font-medium"
                      >
                        Retry Validation & Upload
                      </button>
                    )}
                    <button
                      onClick={() => {
                        setShowValidationModal(false);
                        setManualCloseModal(true);
                      }}
                      className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium"
                    >
                      Close
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Duplicate Detection Modal */}
      {showDuplicateDetection && duplicateCandidates.length > 0 && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-bold text-red-600">
                  ⚠️ Duplicate Assets Detected
                </h3>
                <button
                  onClick={() => setShowDuplicateDetection(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  ✕
                </button>
              </div>
              <p className="text-sm text-gray-600 mb-4">
                The following assets appear to be duplicates. Select the ones
                you want to remove.
              </p>
              <div className="space-y-2 max-h-60 overflow-y-auto mb-4">
                {duplicateCandidates.map((asset) => (
                  <div
                    key={asset.id}
                    className="flex items-center gap-3 p-3 border rounded-lg hover:bg-gray-50"
                  >
                    <input
                      type="checkbox"
                      checked={selectedDuplicates.includes(asset.id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedDuplicates([
                            ...selectedDuplicates,
                            asset.id,
                          ]);
                        } else {
                          setSelectedDuplicates(
                            selectedDuplicates.filter((id) => id !== asset.id),
                          );
                        }
                      }}
                      className="rounded border-gray-300 text-red-600 focus:ring-red-500"
                    />
                    <div className="text-2xl">{getAssetIcon(asset.type)}</div>
                    <div className="flex-1">
                      <p className="font-medium text-gray-800">{asset.name}</p>
                      <p className="text-xs text-gray-500">
                        {formatFileSize(asset.size)}
                      </p>
                      <p className="text-xs text-gray-400">
                        Uploaded: {asset.uploadDate.toLocaleDateString()}
                      </p>
                      <p className="text-xs text-gray-400 font-mono">
                        Asset ID:{" "}
                        {(asset.assetIdentity?.uuid || asset.id).substring(
                          0,
                          12,
                        )}
                        ...
                      </p>
                    </div>
                    <span className="text-xs bg-red-100 text-red-800 px-2 py-1 rounded">
                      Duplicate
                    </span>
                  </div>
                ))}
              </div>
              <div className="flex gap-2 justify-end">
                <button
                  onClick={() => setShowDuplicateDetection(false)}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    setSelectedDuplicates(duplicateCandidates.map((a) => a.id));
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Select All
                </button>
                <button
                  onClick={handleRemoveDuplicates}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                >
                  Remove Selected ({selectedDuplicates.length})
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modals */}
      {renderMetadataModal()}
      {renderVersionHistory()}
      {renderApprovalModal()}
      {renderDebugModal()}
      {renderArchiveConfirmModal()}
    </div>
  );
};

export default AssetUploadComponent;
